import { useDocumentMathMacros } from "@/lib/useMathMacros";
import i18n from "i18next";
import { useParams, useSearchParams, useNavigate, useLocation, Navigate } from "react-router";
import { SAVE_VERSION_EVENT, type SaveVersionDetail } from "@/lib/savedMoment/events";
import { canEdit, isOwn } from "@/lib/documentAccess";
import { importBibtex } from "@/lib/bibliography/importBibtex";
import { useOpenDocumentStore } from "@/store/openDocumentStore";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useDocument } from "@/hooks/useDocument";
import { useDocumentSync } from "@/hooks/useDocumentSync";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import { useEditorContext } from "@/contexts/EditorContext";
import { TopBar, type EditorMode } from "@/components/editor/TopBar";
import { LiliaTopBar } from "@/components/template/lilia-shell/LiliaTopBar";
import { SaveIndicator } from "@/components/editor/saveIndicator/SaveIndicator";
import { type ActivityPanelType } from "@/components/editor/ActivityBar";
import { EditorPanel } from "@/components/editor/EditorPanel";
import { PreviewPanel } from "@/components/editor/PreviewPanel";
import { Tour } from "@/components/onboarding/Tour";
import { ResizeHandle } from "@/components/editor/ResizeHandle";
// StatusBar is no longer mounted from EditorPage; BlockEditor renders it
// inside the document scroll container. EditorPage just pushes the
// status data into editorStore.statusMeta via a useEffect (Pass 3 / 2026-05-09).
// FloatingToolbar is NOT mounted on Flow (2026-07-16) — selection bubble /
// context-style menu removed per user direction. Formatting: ribbon, ⌘K,
// keyboard shortcuts. Component file kept for ToolbarHarness only.
import { CommandPalette, useCommandPaletteShortcut, type CommandAction } from "@/components/editor/CommandPalette";
import {
  useInsertions, useRecentInsertionTokens, insertTokenIntoEditor, tokenKey,
  setInsertionTelemetryFetcher,
} from "@/hooks/useInsertions";
import { FeedbackDialog } from "@/components/feedback/FeedbackWidget";
import { useCurrentSection } from "@/hooks/useCurrentSection";
import useDarkMode from "@/utils/hooks/useDarkMode";
import {
  IconPencil, IconColumns, IconCode, IconLayoutCards, IconFocus2,
  IconCheck, IconArrowLeft, IconArrowRight, IconFileImport, IconDeviceFloppy, IconSun, IconMoon,
  IconPackage, IconWand, IconBraces, IconMessage, IconQuote, IconAlertTriangle,
  IconChevronUp, IconChevronDown, IconListTree, IconX, IconSparkles,
} from "@tabler/icons-react";
import { AskLiliaPanel } from "@/components/editor/AskLiliaPanel";
import { useDocumentCheck } from "@/components/mobile/useDocumentCheck";
import { AskEdgeTab } from "@/components/editor/AskEdgeTab";
import { startAskBridge, type AskBridge } from "@/lib/askBridge";
import { openAskOnTop, openAskWindow, type AskWindowKind } from "@/lib/askWindow";
import { usePublisherProfiles } from "@/hooks/usePublisherProfiles";
import { usePublisherCheck } from "@/components/export/SubmissionPanel";
import { MobileSettingsSheet } from "@/components/mobile/MobileSettingsSheet";
import { MobileLookSheet } from "@/components/mobile/MobileLookSheet";
import { useThemes } from "@/hooks/useThemes";
import { computeTypeLabel, type StampDocumentInput } from "@/components/editor/DocumentStamp/stampText";
import type { DocSettingsPatch, DocSettingsValues } from "@/lib/mobileSettings";
import { MobileLatexView } from "@/components/mobile/MobileLatexView";
import { MobileReader } from "@/components/mobile/MobileReader";
import { MobilePreview } from "@/components/mobile/MobilePreview";
import { useMobileReader } from "@/hooks/useMobileMode";
import { PackagesModal } from "@/components/studio/PackagesModal";
import { OutlinePanel } from "@/components/editor/OutlinePanel";
import { useChatFirstLayout, CHAT_MIN, REOPEN_RAIL_W } from "@/hooks/useChatFirstLayout";
import {
  useReportWorkspaceTopBar,
  useWorkspaceShellOptional,
} from "@/contexts/WorkspaceShellContext";
import {
  CHAT_FIRST_HEADER_H,
  DocumentSurfaceTabs,
  PANE_TABS,
  PaneSurfaceTabs,
  type DocumentSurface,
} from "@/components/editor/ArtifactSurfaceTabs";
import {
  readDefaultPanes,
  selectSurface,
  sideOf,
  widthKeyFor,
  type PaneLayout,
  type PaneSide,
  type PaneSurface,
} from "@/lib/workspacePanes";
import { testIds } from "@/testIds";
import { validationSignature, validationStatus } from "@/components/editor/validationStatus";
import { useCommentCount } from "@/hooks/useComments";
import { MOBILE_MAX_WIDTH, mobileHandoffTarget } from "@/lib/mobileHandoff";
// ds24 Arrangement A (2026-06-28): the old left ActivityBar + SidePanel and
// the right RightRail are consolidated into one 52px LEFT PanelRail carrying
// the six utility panels. Source + preview + Ask Lilia stay on the right.
import { PanelRail, shortcutLabel, type PanelId } from "@/components/editor/PanelRail";
import { SymbolsPanel } from "@/components/editor/SymbolsPanel";
import { StudioFormattingProvider } from "@/contexts/StudioFormattingContext";
import { TOOLBOX_FOCUS_SEARCH_EVENT } from "@/components/toolbox/insertHandler";
import { EditorToolsStrip } from "@/components/editor/EditorToolsStrip";
// DocInfoBar removed 2026-05-13 — DocumentStamp top-right is the
// single doc-info surface on both Flow + Block.
import { ValidateRailPanel } from "@/components/editor/ValidateRailPanel";
import { createStudioApi } from "@/lib/studio-api";
import { DocumentSettingsDialog } from "@/components/editor/DocumentSettingsDialog";
import { ImportLatexModal } from "@/components/editor/ImportLatexModal";
import type { OpenImportLatexDetail } from "@/components/editor/SlashCommandMenu";
import { takeSlashInsertRange } from "@/lib/tiptap/extensions/slashTrigger";
import { BibliographyPanel } from "@/components/editor/BibliographyPanel";
// ElementsPanel + InsertionsPanel removed 2026-05-11 — Toolbox is the
// single insert surface (user direction: "keep only the new insert pane").
import { VersionHistoryPanel } from "@/components/editor/VersionHistoryPanel";
import { CommentsPanel } from "@/components/comments/CommentsPanel";
import { KeyboardShortcutsDialog } from "@/components/editor/KeyboardShortcutsDialog";
import { EditorProvider } from "@/contexts/EditorContext";
import { api, type Block, type DocumentLook, type DocumentWithBlocks } from "@/lib/api";
import { documentChapters, effectiveTheme, normaliseLook } from "@/lib/themes/look";
import { useCanvasLook } from "@/hooks/useCanvasLook";
import { useToast } from "@/components/shared/Toast";
import { useLiveDocumentChanges, type DocumentChangedExternally } from "@/hooks/useLiveDocumentChanges";
import { useEditorStore } from "@/store/editorStore";
import { usePresence } from "@/hooks/usePresence";
import { useValidToken } from "@/hooks/useValidToken";
import { useAuth } from "@/auth";
import { generateUUID } from "@/utils/uuid";
import { blockToTiptapNode } from "@/lib/tiptap/document-serializer";
import { EditorTablePicker } from "@/components/tools/table/EditorTablePicker";
import { useLandOnBlock } from "@/components/tools/table/landedBlock";
import type { JSONContent } from "@tiptap/core";
import { cn } from "@/lib/cn";
import { ReferencesPanel } from "@/components/editor/references/ReferencesPanel";
import { formFor } from "@/components/editor/refPicker/refPickerModel";

/**
 * SG-118 fix: probe mobile breakpoint at module-eval time so the
 * mobile redirect happens at render-time (via <Navigate>), not via
 * useEffect after first paint. Pre-fix, useEffect ran AFTER mounting
 * EditorPage's full editor surface — heavy components (TipTap,
 * ApiContext, useDocument fetch) executed and a brief desktop layout
 * flashed before the studio redirect kicked in. On mobile networks
 * this presented as "small-font incomplete page that fixes on reload".
 *
 * Now: matchMedia is checked inline; if mobile, return <Navigate>
 * synchronously; the heavy editor surface never mounts on mobile.
 */
function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

// The seam between the two panes. Both right-column items carry it — they are
// vertically adjacent, so one continuous edge — and the editor carries it when
// it is the one on the right. All three tokens move together with the pane
// surface; see global.scss.
const SEAM = {
  borderLeft: "var(--lilia-seam-width,1px) solid var(--lilia-seam-color,#e4e9f1)",
  boxShadow: "var(--lilia-seam-shadow, -4px 0 18px rgba(0,0,0,.05))",
} as const;

// PDF and Source both mount PreviewPanel; each offers only the tabs its own
// top-level tab stands for. Out here so the arrays keep their identity across
// renders.
const SIDE_SURFACE_KEY = "lilia-side-surface";
const PDF_ONLY = ["pdf"] as const;
const MARKUP_TABS = ["latex", "lml"] as const;

/** A stable empty list: the phone's check runs only when the reader shows. */
const NO_BLOCKS: readonly never[] = [];

function EditorPageInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile is a separate Vite app — hand off to it, but only if there is one.
  // See lib/mobileHandoff: this used to fall back to localhost:3002 and fire on
  // every viewport at or below 1024px, which meant every phone and tablet got a
  // blank page from a port nobody runs.
  const handoff = typeof window === "undefined" ? null : mobileHandoffTarget({
    origin: import.meta.env.VITE_MOBILE_ORIGIN,
    href: window.location.href,
    isNarrow: isMobileViewport(),
    documentId: id ?? "",
  });
  if (handoff) {
    window.location.replace(handoff);
    return (
      <div className="flex h-screen items-center justify-center text-sm text-[var(--lilia-text-muted)]">
        {i18n.t("editor:ui.openingMobile")}
      </div>
    );
  }

  const { document, isLoading, error, refetch } = useDocument(id!);
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();
  // The maths macros the editor and the PDF share, with this document's own (18 Oct).
  useDocumentMathMacros(id, document?.customPreamble, fetchWithAuth);
  // Register the authed fetcher with the insertion-telemetry queue so
  // recordInsertion (called from all 4 insert surfaces) can POST events
  // to the server without prop-drilling. Re-runs on fetcher identity
  // change but stays a no-op cycle since the underlying token is shared.
  useEffect(() => {
    setInsertionTelemetryFetcher(fetchWithAuth, API_BASE);
    return () => setInsertionTelemetryFetcher(null, "");
  }, [fetchWithAuth]);
  const { insertCitation, insertReference, activeEditor, insertInlineMath } = useEditorContext();
  // Viewers read: no ribbon, no editable canvas, no pushes (the API refuses them).
  const mayEdit = canEdit(document);

  // Ask Lilia's "Apply" on a LaTeX fence: a display equation after the block
  // that holds the caret, as Block mode's did. It was wired only there, so
  // when Block mode went (6 Sep) the Apply button went with it.
  const applyLatexFromChat = useCallback((latex: string) => {
    if (!activeEditor) return;
    const { $to } = activeEditor.state.selection;
    const at = $to.depth >= 1 ? $to.after(1) : activeEditor.state.doc.content.size;
    activeEditor.chain()
      .insertContentAt(at, { type: "equationBlock", attrs: { latex: latex.trim(), equationMode: "display" } })
      .run();
  }, [activeEditor]);
  const { user } = useAuth();
  const getValidToken = useValidToken();

  // Store access
  const blocks = useEditorStore((s) => s.blocks);
  // The document's chapters (numbered level-1 headings): Look's pins.
  const chapters = useMemo(() => documentChapters(blocks), [blocks]);
  // The document theme on the canvas and in the outline (null = Classic).
  const canvasLook = useCanvasLook(document?.look, document?.lookLocked, blocks, document?.lookThemes);
  const { showToast } = useToast();
  // Section breadcrumb for the status whisper (LILIA-117).
  const currentSection = useCurrentSection(blocks as unknown as import("@/lib/api").Block[]);
  const title = useEditorStore((s) => s.title);
  const setTitle = useEditorStore((s) => s.setTitle);
  const initDocument = useEditorStore((s) => s.initDocument);
  const focusedBlockId = useEditorStore((s) => s.focusedBlockId);
  const highlightedCiteKey = useEditorStore((s) => s.highlightedCiteKey);
  const setStatusMeta = useEditorStore((s) => s.setStatusMeta);

  // Auto-open bibliography panel when a citation is clicked
  useEffect(() => {
    if (highlightedCiteKey) {
      setActivePanel("bibliography");
    }
  }, [highlightedCiteKey]);

  // Presence — disabled 2026-04-24 pre-launch. UI avatars are hidden
  // (see TopBar.tsx `false &&`), and connecting the SignalR hub
  // anyway burns requests + surfaces "Handshake was canceled" errors
  // when users navigate quickly (mobile flaky network after import).
  // Flip enabled back to true when sharing returns post-launch.
  const { users: presenceUsers } = usePresence({
    documentId: id!,
    userId: user.userId,
    displayName: user.userName,
    avatarUrl: user.avatar,
    enabled: false,
    accessTokenFactory: getValidToken,
  });

  // URL search params for persisting mode & tab
  const [searchParams, setSearchParams] = useSearchParams();
  // A table put in from Tables (Insert and open, Go to Table N): show where it is.
  useLandOnBlock();

  // One-shot defaults migration (2026-05-14). Runs before any useState
  // initializer reads localStorage so all panel/preview prefs land in
  // the new collapsed-by-default world consistently. The migration is
  // idempotent — flipping `lilia-defaults-v2` to "true" gates it.
  if (typeof window !== "undefined" && localStorage.getItem("lilia-defaults-v2") !== "true") {
    try {
      localStorage.removeItem("lilia-panel-pinned");
      localStorage.removeItem("lilia-active-panel");
      localStorage.removeItem("lilia-panel-explicit");
      localStorage.removeItem("lilia-right-rail-tab");
      localStorage.removeItem("lilia-editor-mode");
      localStorage.setItem("lilia-defaults-v2", "true");
    } catch { /* ignore */ }
  }

  // Editor state
  const [editorMode, setEditorMode] = useState<EditorMode>(() => {
    const mode = searchParams.get("mode");
    if (mode === "split" || mode === "source" || mode === "visual") return mode;
    // Default to distraction-free visual — preview is one click away but
    // doesn't eat half the canvas on first load. Users who lived in split
    // mode can save that preference.
    const savedMode = localStorage.getItem("lilia-editor-mode");
    if (savedMode === "split" || savedMode === "source" || savedMode === "visual") return savedMode;
    return "visual";
  });

  // Disclosure dial (Handoff 19 §4): the ribbon is the Full↔Focused control.
  // Default Full (ribbon visible) — unchanged from today. ⌘. / the collapse
  // chevron drops to Focused: ribbon hidden, format moves to the floating
  // selection toolbar. Persisted so the choice sticks across sessions.
  const [ribbonCollapsed, setRibbonCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("lilia-ribbon-collapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("lilia-ribbon-collapsed", ribbonCollapsed ? "1" : "0");
    } catch { /* ignore */ }
  }, [ribbonCollapsed]);
  // Pass 3 design: outline is a permanently-pinned column beside the
  // activity rail. New users land with outline open + pinned; existing
  // users with a saved preference keep what they had. The dim-when-
  // writing behavior (opacity 60% in mode === "visual") still applies.
  //
  // localStorage keys:
  //   lilia-panel-pinned    — "true" | "false"
  //   lilia-active-panel    — last opened panel (any ActivityPanelType)
  //   lilia-panel-explicit  — "true" if user has ever toggled the panel
  //                            themselves; missing → first-time user gets
  //                            the new pinned-outline default
  // Default panel state direction (2026-05-14): all side panels collapsed
  // by default — the Toolbox is the only always-visible panel because it
  // IS the insert surface. Outline / Comments / History / Validate /
  // Format start collapsed; users open what they want.
  //
  // localStorage keys:
  //   lilia-panel-pinned    — "true" | "false"
  //   lilia-active-panel    — last opened panel
  //   lilia-defaults-v2     — set "true" once we've migrated a user from
  //                            the old "outline pinned by default" world
  //                            to the new "all-collapsed" world. After
  //                            the migration runs once their explicit
  //                            choices stick.
  const [activePanel, setActivePanel] = useState<ActivityPanelType>(() => {
    // Migration above already cleared keys for v1 → v2 users; existing
    // v2 users keep their explicit choices. Default is collapsed (null)
    // unless the user has previously pinned a panel open.
    const pinned = localStorage.getItem("lilia-panel-pinned");
    const lastPanel = localStorage.getItem("lilia-active-panel") as ActivityPanelType | null;
    // "elements" now restores like any other panel — it renders the
    // Toolbox insert surface (gated below). "insertions" is still a
    // dead panel (no SidePanel content) so keep it filtered.
    if (lastPanel === "insertions") {
      try { localStorage.removeItem("lilia-active-panel"); } catch { /* */ }
      return null;
    }
    return pinned === "true" && lastPanel ? lastPanel : null;
  });
  const [isPanelPinned, setIsPanelPinned] = useState(() => {
    return localStorage.getItem("lilia-panel-pinned") === "true";
  });
  // Right-rail (Pass 3 / Desktop workflows.html #10). 280px persistent
  // column with 3 tabs — Outline / Comments / Validate. Toggleable
  // per-tab (Mod+1/2/3); active tab persists across reloads. Hidden in
  // focus mode along with the activity rail + side panel.
  // ds24 Arrangement A: the single LEFT utility rail's open panel. One panel
  // at a time; null = collapsed (the default load state). PanelRail owns the
  // ⌘1-6 toggles internally, so the old window-level rail shortcut is gone.
  const [activeRailTab, setActiveRailTab] = useState<PanelId | null>(() => {
    try {
      const stored = localStorage.getItem("lilia-left-panel");
      if (
        stored === "outline" || stored === "refs" || stored === "xrefs" || stored === "syms" ||
        stored === "review" || stored === "validate" || stored === "history"
      ) return stored;
    } catch { /* ignore */ }
    return null; // collapsed by default
  });
  const setRailTab = useCallback((tab: PanelId | null) => {
    setActiveRailTab(tab);
    try {
      if (tab) localStorage.setItem("lilia-left-panel", tab);
      else localStorage.removeItem("lilia-left-panel");
    } catch { /* ignore */ }
  }, []);

  // Open the Validate rail tab + best-effort scroll to first issue.
  // Reused by Mod+Shift+V and the TopBar pip popover button.
  const openValidateRail = useCallback(() => {
    setRailTab("validate");
    try {
      const firstError = window.document.querySelector<HTMLElement>(
        "[data-block-id][data-validation-error='true']",
      );
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch { /* ignore */ }
  }, [setRailTab]);

  // Paste-to-fix's "Look up ℵ": open Symbols with the character in the search
  // field. `seq` makes a second lookup of the same character still apply.
  const [symbolsQuery, setSymbolsQuery] = useState<{ text: string; seq: number } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const query = (e as CustomEvent<{ query?: string }>).detail?.query;
      if (!query) return;
      e.preventDefault();
      setSymbolsQuery((prev) => ({ text: query, seq: (prev?.seq ?? 0) + 1 }));
      setRailTab("syms");
    };
    window.addEventListener("lilia:symbols-lookup", handler);
    return () => window.removeEventListener("lilia:symbols-lookup", handler);
  }, [setRailTab]);

  // Listen for the TopBar pip popover "Validate document" button.
  // The button dispatches lilia:open-validate-rail with cancelable=true;
  // calling preventDefault signals "I handled it" so TopBar skips the
  // /studio fallback navigation.
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      openValidateRail();
    };
    window.addEventListener("lilia:open-validate-rail", handler);
    return () => window.removeEventListener("lilia:open-validate-rail", handler);
  }, [openValidateRail]);

  // Focus mode removed 2026-05-14 (user direction: "remove the focus
  // mode"). Was a Mod+. toggle that hid the rails + dashboard chrome
  // for distraction-free writing. Replaced by the new collapsed-by-
  // default panel behavior — users get a clean canvas already, no
  // need for a separate mode that conflicts with their open-panel
  // intent. The legacy `lilia-focus-mode` body class + localStorage
  // key are best-effort cleared once at mount in case stale state
  // persisted from before the removal.
  useEffect(() => {
    try {
      globalThis.document.body.classList.remove("lilia-focus-mode");
      localStorage.removeItem("lilia-focus-mode");
    } catch { /* ignore */ }
  }, []);

  // EditorRibbon Review / View tab event listeners — wired to the
  // corresponding rail tab / mode toggle so clicks do the same thing
  // as the rail's ⌘-digit shortcuts and ⌘\. The toggle-focus-mode
  // event is no longer wired (focus mode removed); listener kept for
  // compatibility with any cached editor bundles still dispatching it.
  useEffect(() => {
    // The rail calls Comments "review"; "comments" was never a slot, so this
    // event used to open nothing.
    const openComments = (e: Event) => { e.preventDefault(); setRailTab("review"); };
    const openOutline = (e: Event) => { e.preventDefault(); setRailTab("outline"); };
    const openHistory = (e: Event) => { e.preventDefault(); setRailTab("history"); };
    const togglePreview = (e: Event) => {
      e.preventDefault();
      setEditorMode((prev) => (prev === "split" ? "visual" : "split"));
    };
    window.addEventListener("lilia:open-comments-rail", openComments);
    window.addEventListener("lilia:open-outline-rail", openOutline);
    window.addEventListener("lilia:open-history-rail", openHistory);
    window.addEventListener("lilia:toggle-preview", togglePreview);
    // The LaTeX view's "Edit in Document settings" (DESIGN-14): open the dialog at the preamble.
    const openSettings = (e: Event) => {
      setShowSettingsDialog(true);
      if ((e as CustomEvent<{ focus?: string }>).detail?.focus === "preamble") {
        window.setTimeout(() => {
          const el = window.document.querySelector<HTMLTextAreaElement>('[data-testid="document-custom-preamble"]');
          el?.scrollIntoView({ block: "center" });
          el?.focus();
        }, 150);
      }
    };
    window.addEventListener("lilia:open-document-settings", openSettings);
    return () => {
      window.removeEventListener("lilia:open-comments-rail", openComments);
      window.removeEventListener("lilia:open-outline-rail", openOutline);
      window.removeEventListener("lilia:open-history-rail", openHistory);
      window.removeEventListener("lilia:toggle-preview", togglePreview);
      window.removeEventListener("lilia:open-document-settings", openSettings);
    };
  }, [setRailTab]);

  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showImportLatex, setShowImportLatex] = useState(false);
  // Any surface (slash menu, AddBlockMenu, toolbox, …) can request the
  // LaTeX import modal by dispatching this window event. Keeps the
  // trigger reachable from everywhere without prop-drilling.
  // Opened from the slash menu, the import lands where the `/` was rather
  // than at the end of the document.
  const importAtSlashRef = useRef(false);
  useEffect(() => {
    const open = (e: Event) => {
      importAtSlashRef.current = !!((e as CustomEvent<OpenImportLatexDetail | undefined>).detail?.atSlash);
      setShowImportLatex(true);
    };
    window.addEventListener("openImportLatex", open);
    return () => window.removeEventListener("openImportLatex", open);
  }, []);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [showPackagesModal, setShowPackagesModal] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Inline document-level validation for the Validate rail — run it here
  // without leaving Flow for the full page (founder request). Per-block detail
  // still lives behind "Open full validation". (fetchWithAuth from useApiContext above.)
  const studioApi = useMemo(() => createStudioApi(fetchWithAuth), [fetchWithAuth]);
  const [flowValidation, setFlowValidation] = useState<{ valid: boolean; error?: string; warnings: string[] } | null>(null);
  /**
   * The document a validation pass ran against.
   *
   * A pass only speaks for the state it saw. Without this, a clean result kept
   * showing green after the document changed underneath it — the same false
   * reassurance as claiming "valid" for a document that was merely saved.
   */
  const [validatedSignature, setValidatedSignature] = useState<string | null>(null);
  // Current blocks, readable from inside an await without making the callback
  // depend on them.
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;

  // The rail's warning dots: open comments on Comments, a failing or warning
  // validation on Validate. A stale pass is not a warning, only an old answer.
  const { data: commentCount } = useCommentCount(id ?? "");
  const railValidateWarn = ["error", "warning"].includes(validationStatus(flowValidation));
  const [isValidatingFlow, setIsValidatingFlow] = useState(false);
  const runFlowValidation = useCallback(async () => {
    if (!id) return;
    setIsValidatingFlow(true);
    try {
      setFlowValidation(await studioApi.validateDocument(id));
      // Captured when the result ARRIVES, not when the pass started.
      //
      // The stored blocks drift on their own while a pass runs — TipTap
      // normalises content on adoption, so an untouched document can end the
      // pass with a different signature than it began with. Taking the
      // baseline at the start made every fresh pass read "changed since
      // validation" immediately, which is a worse lie than the one being
      // fixed. The cost is a genuine edit made DURING the pass being
      // attributed to it; that window is seconds, and the pass would have
      // missed the edit anyway.
      setValidatedSignature(validationSignature(blocksRef.current));
    } catch {
      setFlowValidation({ valid: false, error: "Validation failed", warnings: [] });
      setValidatedSignature(validationSignature(blocksRef.current));
    } finally {
      setIsValidatingFlow(false);
    }
  }, [id, studioApi]);

  // Ask Lilia first-class panel (handoff 25): open state persisted per-document,
  // dock⇄overlay presentation persisted globally.
  const [showAskLilia, setShowAskLiliaRaw] = useState(false);
  const [askMode, setAskMode] = useState<"dock" | "overlay">(() => {
    try { return localStorage.getItem("lilia-ask-mode") === "overlay" ? "overlay" : "dock"; } catch { return "dock"; }
  });
  // Restore per-doc open state when the document changes.
  useEffect(() => {
    if (!id) return;
    // CD §4/§8: Ask Lilia is OPEN by default (docked). Only stay closed if the
    // user explicitly closed it for this doc ("0"); unset → open.
    try { setShowAskLiliaRaw(localStorage.getItem(`lilia-ask-open-${id}`) !== "0"); } catch { setShowAskLiliaRaw(true); }
  }, [id]);
  const setShowAskLilia = useCallback((open: boolean) => {
    setShowAskLiliaRaw(open);
    try { if (id) localStorage.setItem(`lilia-ask-open-${id}`, open ? "1" : "0"); } catch { /* ignore */ }
  }, [id]);
  const toggleAskMode = useCallback(() => {
    setAskMode((m) => { const next = m === "dock" ? "overlay" : "dock"; try { localStorage.setItem("lilia-ask-mode", next); } catch { /* ignore */ } return next; });
  }, []);

  // Chat-First layout — prefer shared workspace shell so Primary/Side is one state.
  const workspaceShell = useWorkspaceShellOptional();
  const localChatLayout = useChatFirstLayout({ leftPanelOpen: activeRailTab != null });
  const chatLayout = workspaceShell?.chatLayout ?? localChatLayout;
  const {
    layout: arrangement,
    setLayout: setArrangementRaw,
    isChatLayout,
    artifactOpen,
    openArtifact,
    closeArtifact,
    artifactWidth,
    setArtifactWidth,
    setWidthSlot,
    onArtifactResize,
  } = chatLayout;

  const setArrangement = useCallback((next: "chat" | "side") => {
    setArrangementRaw(next);
    if (next === "chat") {
      setShowAskLilia(true);
      // Primary is chat + document only — never three full columns.
      // Opening source later demotes chat to the Side dock.
      setEditorMode((m) => {
        if (m === "visual") return m;
        try { localStorage.setItem("lilia-editor-mode", "visual"); } catch { /* ignore */ }
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("mode");
          window.history.replaceState(null, "", url.toString());
        } catch { /* ignore */ }
        return "visual";
      });
    }
  }, [setArrangementRaw, setShowAskLilia]);

  // ── Ask Lilia in its own window (Olivia, 10 Oct, handoff-ask-lilia-window) ──
  // A new window or Keep on top. While it is out, the editor is full width with
  // a 32 px tab on the right edge; closing it brings Ask Lilia back docked, with
  // the conversation (kept on the server) intact. The two windows talk over
  // lib/askBridge: Lilia's edits reload this editor as they would in one page.
  const [askOut, setAskOut] = useState<AskWindowKind | null>(null);
  const [askWorking, setAskWorking] = useState(false);
  const askWin = useRef<Window | null>(null);
  const askBridgeRef = useRef<AskBridge | null>(null);
  /** The arrangement Ask Lilia left: Bring back restores it (half and half, or docked). */
  const askReturnTo = useRef<"chat" | "side">("side");
  const askBack = useCallback(() => {
    if (askWin.current && !askWin.current.closed) askWin.current.close();
    askWin.current = null;
    setAskOut(null);
    setAskWorking(false);
    if (askReturnTo.current === "chat") setArrangement("chat");
    else setShowAskLilia(true);
  }, [setShowAskLilia, setArrangement]);
  useEffect(() => {
    if (!id) return;
    const b = startAskBridge(id, "editor", (signal) => {
      // Ask Lilia opened elsewhere (a window, or its URL): one at a time, so the dock steps aside.
      if (signal === "opened") setAskOut((cur) => cur ?? "window");
      else askBack();
    });
    askBridgeRef.current = b;
    // The editor going away takes the other window with it.
    const onHide = () => b?.signal("closed");
    window.addEventListener("pagehide", onHide);
    return () => {
      window.removeEventListener("pagehide", onHide);
      b?.close();
      askBridgeRef.current = null;
    };
  }, [id, askBack]);
  // A window closed without a word (the OS close box before the page could speak).
  useEffect(() => {
    if (askOut !== "window") return;
    const t = window.setInterval(() => {
      if (askWin.current?.closed) askBack();
    }, 1000);
    return () => window.clearInterval(t);
  }, [askOut, askBack]);
  // Lilia working in the other window: the edge tab's dot. Its LaTeX fences land here.
  useEffect(() => {
    const onEditing = (e: Event) => {
      const d = (e as CustomEvent<{ documentId?: string; active?: boolean }>).detail;
      if (d?.documentId === id) setAskWorking(!!d.active);
    };
    const onLatex = (e: Event) => {
      const d = (e as CustomEvent<{ documentId?: string; latex?: string }>).detail;
      if (d?.documentId === id && d.latex) void applyLatexFromChat(d.latex);
    };
    window.addEventListener("lilia:ai-editing", onEditing);
    window.addEventListener("lilia:ask-apply-latex", onLatex);
    return () => {
      window.removeEventListener("lilia:ai-editing", onEditing);
      window.removeEventListener("lilia:ask-apply-latex", onLatex);
    };
  }, [id, applyLatexFromChat]);
  const moveAsk = useCallback(
    async (how: AskWindowKind) => {
      if (!id) return;
      let win: Window | null = null;
      try {
        win = how === "top" ? await openAskOnTop(id) : openAskWindow(id);
      } catch {
        win = null;
      }
      if (!win) {
        showToast("The browser didn't open the window. Allow pop-ups for this site, then try again.", "error");
        return;
      }
      askWin.current = win;
      askReturnTo.current = isChatLayout ? "chat" : "side";
      setAskOut(how);
      // Primary has Ask Lilia as a pane: the document takes the room.
      if (isChatLayout) setArrangementRaw("side");
      if (how === "top") win.addEventListener("pagehide", () => askBack());
    },
    [id, isChatLayout, setArrangementRaw, askBack, showToast],
  );
  // Back to Primary (chat as a pane) while Ask Lilia is out: it comes back, so there is one.
  const wasChat = useRef(isChatLayout);
  useEffect(() => {
    if (isChatLayout && !wasChat.current && askOut) askBack();
    wasChat.current = isChatLayout;
  }, [isChatLayout, askOut, askBack]);
  const focusAsk = useCallback(async () => {
    const w = askWin.current;
    if (!w || w.closed) return false;
    w.focus();
    await new Promise((r) => setTimeout(r, 300));
    return !window.document.hasFocus();
  }, []);

  // If we restored a source/split mode while arrangement was still Primary
  // (e.g. localStorage), demote chat so we never paint three full columns.
  // Do NOT force Ask Lilia open — source works with AI collapsed (re-open rail).
  useEffect(() => {
    if (isChatLayout && (editorMode === "split" || editorMode === "source")) {
      setArrangementRaw("side");
    }
  }, [isChatLayout, editorMode, setArrangementRaw]);

  // "Ask Lilia: improve" (floating selection toolbar, Handoff 26) — open the
  // dock so the panel (which prefills itself from the same event) is visible.
  // "document-applied" (architect ops applied) → ensure the artifact pane is
  // open so the author sees the update ("present as you go").
  useEffect(() => {
    const onImprove = () => {
      setShowAskLilia(true);
      if (isChatLayout) openArtifact();
    };
    const onApplied = () => {
      if (isChatLayout) openArtifact();
    };
    window.addEventListener("lilia:ask-improve", onImprove);
    window.addEventListener("lilia:document-applied", onApplied);
    return () => {
      window.removeEventListener("lilia:ask-improve", onImprove);
      window.removeEventListener("lilia:document-applied", onApplied);
    };
  }, [setShowAskLilia, isChatLayout, openArtifact]);
  useCommandPaletteShortcut(setPaletteOpen);

  // Resizable panel widths
  const [sidePanelWidth, setSidePanelWidth] = useState(() => {
    const saved = localStorage.getItem("lilia-sidepanel-width");
    return saved ? Number(saved) : 280;
  });
  const [previewPanelWidth, setPreviewPanelWidth] = useState<number | null>(() => {
    const saved = localStorage.getItem("lilia-preview-width");
    return saved ? Number(saved) : null;
  });
  const [previewPosition, setPreviewPosition] = useState<"right" | "bottom">(() => {
    const saved = localStorage.getItem("lilia-preview-position");
    if (saved === "right" || saved === "bottom") return saved;
    return "right";
  });
  const [previewPanelHeight, setPreviewPanelHeight] = useState<number | null>(() => {
    const saved = localStorage.getItem("lilia-preview-height");
    return saved ? Number(saved) : null;
  });

  // On mobile, always use bottom layout
  const isMobileView = typeof window !== "undefined" && window.innerWidth < 768;
  const effectivePreviewPosition = isMobileView ? "bottom" : previewPosition;

  const handlePreviewPanelResizeVertical = useCallback((delta: number) => {
    setPreviewPanelHeight((prev) => {
      const current = prev ?? window.innerHeight * 0.4;
      const maxH = window.innerHeight * 0.7;
      const next = Math.min(maxH, Math.max(150, current - delta));
      localStorage.setItem("lilia-preview-height", String(Math.round(next)));
      return Math.round(next);
    });
  }, []);

  const handleSidePanelResize = useCallback((delta: number) => {
    setSidePanelWidth((prev) => {
      const next = Math.min(500, Math.max(200, prev + delta));
      localStorage.setItem("lilia-sidepanel-width", String(next));
      return next;
    });
  }, []);

  const handlePreviewPanelResize = useCallback((delta: number) => {
    setPreviewPanelWidth((prev) => {
      const current = prev ?? window.innerWidth * 0.35;
      const maxW = window.innerWidth * 0.5;
      const next = Math.min(maxW, Math.max(300, current - delta));
      localStorage.setItem("lilia-preview-width", String(Math.round(next)));
      return Math.round(next);
    });
  }, []);

  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  // LILIA-137: dropped "typst" and "typst-preview" tabs (soft-renderer
  // path that didn't actually compile Typst). Existing URLs that point
  // at those tabs fall back to the LaTeX tab silently.
  const [previewTab, setPreviewTab] = useState<"latex" | "lml" | "pdf">(() => {
    const tab = searchParams.get("tab");
    if (tab === "latex" || tab === "lml" || tab === "pdf") return tab;
    // Preview surface lands on PDF (document output), not LaTeX source.
    return "pdf";
  });
  // Which markup was last read. previewTab also holds "pdf", which is not an
  // answer to "LaTeX or LML", so Source needs its own memory or a trip through
  // the PDF surface silently drops you back to LaTeX.
  //
  // A ref rather than state: setArtifactSurface reads it, and taking previewTab
  // as a dependency there would rebuild the callback — and rebind the ⌘\ keymap
  // that closes over it — on every tab change.
  /**
   * What each pane is showing.
   *
   * One piece of state for the whole workspace. It replaces a boolean for
   * "is the editor on the left" plus a separate right-hand surface, which were
   * two facts that had to agree and occasionally did not.
   *
   * Starts from the default the reader chose, and is NOT written back. Moving a
   * surface is for the session you move it in; the setting is what you get every
   * time you open a document. Persisting the live arrangement instead would
   * overwrite that setting on the first tab click and it would never be honoured
   * again — which is the difference between a default and a first-run seed.
   *
   * The rules — swap on collision, never the same surface twice — live in
   * lib/workspacePanes, so they are testable without a browser.
   */
  const [panes, setPanes] = useState<PaneLayout>(
    () => readDefaultPanes((k) => localStorage.getItem(k)),
  );

  const markupTabRef = useRef<"latex" | "lml">(previewTab === "lml" ? "lml" : "latex");
  if (previewTab === "latex" || previewTab === "lml") markupTabRef.current = previewTab;

  /**
   * What the document column shows in the Side arrangement.
   *
   * Separate from `panes`, which governs Primary. Side has only one column to
   * fill — Ask Lilia is docked at the right edge and is not one of the things
   * that column can become — so it needs its own three-way choice rather than
   * the four-way one.
   */
  const [sideSurface, setSideSurfaceRaw] = useState<DocumentSurface>(() => {
    try {
      const v = localStorage.getItem(SIDE_SURFACE_KEY);
      if (v === "edit" || v === "pdf" || v === "source") return v;
    } catch { /* ignore */ }
    return "edit";
  });
  const setSideSurface = useCallback((next: DocumentSurface) => {
    setSideSurfaceRaw(next);
    try { localStorage.setItem(SIDE_SURFACE_KEY, next); } catch { /* ignore */ }
    if (next === "pdf") setPreviewTab("pdf");
    if (next === "source") setPreviewTab(markupTabRef.current);
  }, []);

  /** Where a surface is, or null if it is nowhere. Null means "not on screen". */
  const paneOf = useCallback((surface: PaneSurface): PaneSide | null => {
    const side = sideOf(panes, surface);
    // A collapsed right pane is not showing anything, whatever it remembers.
    if (side === "right" && !artifactOpen) return null;
    return side;
  }, [panes, artifactOpen]);

  const choose = useCallback((side: PaneSide, surface: PaneSurface) => {
    const next = selectSurface(panes, side, surface);
    setPanes(next);
    // Picking anything for the right pane implies wanting to see it.
    if (side === "right") openArtifact();
    // Source remembers which markup you were reading, so leaving and coming
    // back does not drop you from LML into LaTeX.
    if (surface === "source") {
      setPreviewTab(markupTabRef.current);
      try {
        const url = new URL(window.location.href);
        if (markupTabRef.current === "latex") url.searchParams.delete("tab");
        else url.searchParams.set("tab", markupTabRef.current);
        window.history.replaceState(null, "", url.toString());
      } catch { /* ignore */ }
    }
    if (surface === "pdf") setPreviewTab("pdf");
  }, [panes, setPanes, openArtifact]);

  /**
   * Which split this arrangement wears.
   *
   * Keyed by the pair, because the pair is the question — see widthKeyFor.
   * Every arrangement starts at half and remembers where it was dragged to.
   */
  useEffect(() => {
    setWidthSlot(widthKeyFor(panes));
  }, [panes, setWidthSlot]);

  // Sync mode & tab changes to URL.
  // Layout rule (chat-first): never three full-width panes.
  //   Primary = chat + document artifact (Flow or Source surface)
  //   Source beside the document on Side → classic split (document + PreviewPanel)
  const handleModeChange = useCallback((mode: EditorMode) => {
    setEditorMode(mode);
    try { localStorage.setItem("lilia-editor-mode", mode); } catch { /* ignore */ }
    // Update URL without triggering scroll — use history.replaceState directly
    const url = new URL(window.location.href);
    if (mode === "visual") {
      url.searchParams.delete("mode");
    } else {
      url.searchParams.set("mode", mode);
    }
    window.history.replaceState(null, "", url.toString());

    if (mode === "split" || mode === "source") {
      // Side arrangement frees the main row for document + source.
      // Leave Ask Lilia open/closed as the user left it.
      setArrangementRaw("side");
    }
  }, [setArrangementRaw]);

  const handleTabChange = useCallback((tab: "latex" | "lml" | "pdf") => {
    setPreviewTab(tab);
    const url = new URL(window.location.href);
    if (tab === "latex") {
      url.searchParams.delete("tab");
    } else {
      url.searchParams.set("tab", tab);
    }
    window.history.replaceState(null, "", url.toString());
  }, []);

  // Editor command bus — Ask Lilia can launch doc-level actions that land
  // visibly: validate (rail), show source/PDF (Primary: artifact surface;
  // Side: split), export, find. See lib/editorCommands.ts.
  useEffect(() => {
    const onCommand = (e: Event) => {
      const cmd = (e as CustomEvent<{ command?: string }>).detail?.command;
      switch (cmd) {
        case "validate":
          openValidateRail();
          break;
        case "preview":
          if (isChatLayout) {
            // Ask Lilia asking for a preview means the PDF, on the right. If
            // the PDF is already on the left, choose() swaps rather than
            // duplicating it.
            choose("right", "pdf");
          } else {
            setEditorMode("split");
            handleTabChange("pdf");
          }
          break;
        case "export":
          navigate(
            `/import?tab=export${id ? `&documentId=${id}` : ""}${title ? `&documentTitle=${encodeURIComponent(title)}` : ""}`,
          );
          break;
        case "find":
          setPaletteOpen(true);
          break;
      }
    };
    window.addEventListener("lilia:editor-command", onCommand);
    return () => window.removeEventListener("lilia:editor-command", onCommand);
  }, [openValidateRail, setEditorMode, navigate, id, title, isChatLayout, openArtifact, choose, handleTabChange]);

  // Document settings state
  const [docColumns, setDocColumns] = useState(1);
  const [docColumnGap, setDocColumnGap] = useState(1.5);
  const [docColumnSeparator, setDocColumnSeparator] = useState("none");

  // Initialize store from document — once per document id. A later
  // query-cache refresh for the same doc must NOT re-run initDocument:
  // that would reset editVersion and clobber unsynced edits. Deliberate
  // reloads (version restore, conflict "use latest") call initDocument
  // directly. See architecture/2026-05-21-flow-editor-save-model.md.
  const initedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (document && initedIdRef.current !== document.id) {
      initedIdRef.current = document.id;
      initDocument(document);
      setDocColumns(document.columns ?? 1);
      setDocColumnGap(document.columnGap ?? 1.5);
      setDocColumnSeparator(document.columnSeparator ?? "none");
    }
  }, [document, initDocument]);

  // The bibliography is not the blocks: it changes through the panel (import,
  // add, delete) while the store's document stays as initDocument left it —
  // so the @ picker's library, which reads the store, never saw an entry added
  // this session, and "@cite{" opened nothing. Only that field is copied: the
  // blocks and edit versions are the sync worker's and must not be reset here.
  useEffect(() => {
    if (!document?.bibliography) return;
    const current = useEditorStore.getState().document;
    if (current && current.id === document.id && current.bibliography !== document.bibliography) {
      useEditorStore.setState({ document: { ...current, bibliography: document.bibliography } });
    }
  }, [document?.id, document?.bibliography]);

  // Continuous background sync — replaces the debounced autosave. The
  // worker owns the editVersion subscription, write-throughs every edit
  // to an IndexedDB buffer, and pushes the working copy with the
  // document's optimistic-concurrency version.
  // architecture/2026-05-21-flow-editor-save-model.md
  // Source pane is server-rendered LaTeX — tight sync so it stays live
  // while open beside the visual editor (LILIA-138).
  const previewRealtime = editorMode === "split" || editorMode === "source";

  const {
    status: autoSaveStatus,
    lastSavedAt,
    isDirty,
    resolveConflict,
    createCheckpoint,
    flushNow,
    adoptServerDocument,
  } = useDocumentSync({
    documentId: id!,
    document,
    realtime: previewRealtime,
    // Pause sync while Flow is kept alive but hidden under Block.
    enabled: true,
    onSynced: () => {
      // Refresh the active preview tab after each sync (LILIA-138). In
      // realtime mode skip the settle delay — the API already produced
      // the source from the just-synced blocks; otherwise keep it so a
      // later PDF compile has time to finish.
      // Mode-switch path does not rely on this dual-view realtime churn;
      // AI apply + explicit surfaces own refresh now.
      setTimeout(() => setPreviewRefreshKey((k) => k + 1), previewRealtime ? 0 : 1000);
    },
  });


  // Feed the document's own body size to the canvas.
  //
  // The canvas has always rendered in points — it was pinned at 11pt no matter
  // what the document said, so a 10pt and a 12pt document looked identical on
  // screen while their PDFs differed. Everything that scales off this reads
  // --lilia-doc-font-size, so setting it here is the whole change.
  //
  // Set on the root element rather than a wrapper because EditorPage mounts the
  // canvas from three places (flow, split, and the plain surface) and custom
  // properties inherit. Cleared on unmount so leaving the editor does not leave
  // a document's size behind on the rest of the app. `window.document` is
  // explicit: `document` in this file is the Lilia document, not the DOM one.
  const docFontSize = document?.fontSize;
  useEffect(() => {
    const root = window.document.documentElement;
    if (!docFontSize) {
      root.style.removeProperty("--lilia-doc-font-size");
      return;
    }
    root.style.setProperty("--lilia-doc-font-size", `${docFontSize}pt`);
    return () => {
      root.style.removeProperty("--lilia-doc-font-size");
    };
  }, [docFontSize]);

  useEffect(() => {
    if (!workspaceShell) return;
    const open = () => setPaletteOpen(true);
    window.addEventListener("lilia:workspace-open-palette", open);
    return () => window.removeEventListener("lilia:workspace-open-palette", open);
  }, [workspaceShell]);

  // Returning to Flow after Block: re-adopt server document into TipTap.
  // Listen always while mounted (page may be hidden under Block when the event fires).
  useEffect(() => {
    if (!id) return;
    const onMode = async (e: Event) => {
      const detail = (e as CustomEvent).detail as { mode?: string; documentId?: string } | undefined;
      if (detail?.mode !== "flow" || detail.documentId !== id) return;
      try {
        const result = await refetch();
        if (result.data) adoptServerDocument(result.data);
      } catch { /* soft-fail */ }
      setPreviewRefreshKey((k) => k + 1);
    };
    window.addEventListener("lilia:workspace-mode", onMode as EventListener);
    return () => window.removeEventListener("lilia:workspace-mode", onMode as EventListener);
  }, [id, refetch, adoptServerDocument]);

  // Push status-whisper data into the store so BlockEditor can render
  // <StatusBar /> below the document body (Pass 3 / 2026-05-09 round 2).
  useEffect(() => {
    setStatusMeta({
      saveStatus: autoSaveStatus,
      hasUnsaved: isDirty,
      wordCount: countWordsInBlocks(blocks),
      currentSection,
      lastSavedAt,
    });
  }, [autoSaveStatus, isDirty, blocks, currentSection, lastSavedAt, setStatusMeta]);
  useEffect(() => () => setStatusMeta(null), [setStatusMeta]);

  // An app (Claude… over MCP) changed this document: the editor reloads it, names the app and
  // offers Undo (the version saved before its first change). With typing not yet saved, it
  // waits for the author instead of replacing their work (the user, 12 Oct: SignalR with MCP).
  const isDirtyRef = useRef(isDirty);
  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);
  const onExternalChange = useCallback(
    (evt: DocumentChangedExternally) => {
      if (!id) return;
      const reload = () =>
        window.dispatchEvent(new CustomEvent("lilia:document-applied", { detail: { documentId: id, changedBlockIds: evt.blockIds } }));
      if (isDirtyRef.current) {
        showToast(i18n.t("editor:liveChange.changedUnsaved", { app: evt.app }), "info", {
          action: { key: "load", label: i18n.t("editor:liveChange.load"), onClick: reload },
          durationMs: 15000,
        });
        return;
      }
      reload();
      const undoVersionId = evt.undoVersionId;
      showToast(
        i18n.t("editor:liveChange.changed", { app: evt.app }),
        "info",
        undoVersionId
          ? {
              durationMs: 12000,
              action: {
                key: "undo",
                label: i18n.t("editor:liveChange.undo"),
                onClick: async () => {
                  try {
                    await api.documents.versions.restore(id, undoVersionId);
                    reload();
                    showToast(i18n.t("editor:liveChange.undone", { app: evt.app }), "success");
                  } catch {
                    showToast(i18n.t("editor:liveChange.undoFailed"), "error");
                  }
                },
              },
            }
          : undefined,
      );
    },
    [id, showToast],
  );
  useLiveDocumentChanges(id, getValidToken, onExternalChange);

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        // Bytes are already safe via continuous sync — ⌘S cuts a named
        // version (a history checkpoint), not a "save".
        e.preventDefault();
        void createCheckpoint();
      }
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        handleModeChange(editorMode === "split" ? "visual" : "split");
      }
      // Document-level undo/redo (only when no TipTap editor is focused)
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        const active = globalThis.document.activeElement;
        const inTiptap = active?.closest(".ProseMirror");
        if (!inTiptap) {
          e.preventDefault();
          useEditorStore.getState().undo();
        }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.shiftKey && e.key === "z") || (e.shiftKey && e.key === "Z"))) {
        const active = globalThis.document.activeElement;
        const inTiptap = active?.closest(".ProseMirror");
        if (!inTiptap) {
          e.preventDefault();
          useEditorStore.getState().redo();
        }
      }
      // ⌘. — Focus preset (Handoff 19 §4): toggle the ribbon (Full↔Focused).
      // Restored 2026-06-13 as the disclosure dial; collapsed → the floating
      // selection toolbar takes over formatting.
      if ((e.ctrlKey || e.metaKey) && e.key === "." && !e.shiftKey && !e.altKey) {
        e.preventDefault();
        setRibbonCollapsed((v) => !v);
        return;
      }
      // ⌘\ — View source.
      // Primary (chat): cycle Flow → Split → Source → Flow (never demotes layout).
      // Side: classic document + PreviewPanel split.
      if ((e.ctrlKey || e.metaKey) && e.key === "\\") {
        e.preventDefault();
        if (isChatLayout) {
          openArtifact();
          // Cycle the right pane, skipping whatever the left pane is holding —
          // selecting that would swap the panes rather than advance the cycle,
          // which is not what a repeated keypress should do.
          const order = PANE_TABS.map((t) => t.id as PaneSurface)
            .filter((sfc) => sfc !== panes.left);
          const i = order.indexOf(panes.right);
          choose("right", order[(i + 1) % order.length]!);
        } else {
          handleModeChange(editorMode === "visual" ? "split" : "visual");
        }
        return;
      }
      // Right-rail tab toggles: Mod+1/2/3/4 (Pass 3 / Desktop
      // workflows keymap section). Tap again to collapse the rail body.
      // ⌘1-6 rail toggles are handled inside PanelRail (ds24). Removed the
      // duplicate window-level railMap so the two don't fight.
      // Mod+Shift+V — open the Validate tab + scroll to the first
      // issue block. Per Desktop workflows.html acceptance #6. Not while
      // writing: there it is paste as plain text, as everywhere else.
      const inText = (e.target as HTMLElement | null)?.closest?.('[contenteditable="true"], input, textarea');
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "v" || e.key === "V") && !inText) {
        e.preventDefault();
        openValidateRail();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [title, blocks, createCheckpoint, editorMode, handleModeChange, activeRailTab, setRailTab, isChatLayout, openArtifact, panes, choose]);

  // The save indicator's card: "Save a version…" asks for the same named
  // version as ⌘S, with the name the author typed (saved-moment handoff §3).
  useEffect(() => {
    const onSaveVersion = (e: Event) => {
      const name = (e as CustomEvent<SaveVersionDetail>).detail?.name ?? null;
      void createCheckpoint(name);
    };
    window.addEventListener(SAVE_VERSION_EVENT, onSaveVersion);
    return () => window.removeEventListener(SAVE_VERSION_EVENT, onSaveVersion);
  }, [createCheckpoint]);

  const handleTogglePin = useCallback(() => {
    setIsPanelPinned((prev) => {
      const next = !prev;
      localStorage.setItem("lilia-panel-pinned", String(next));
      // Mark that the user has made an explicit decision so next-visit
      // honors what they chose instead of the new-user default.
      localStorage.setItem("lilia-panel-explicit", "true");
      return next;
    });
  }, []);

  // Auto-hide unpinned panel when editor area is focused
  useEffect(() => {
    if (isPanelPinned || !activePanel) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // The Insert (Toolbox) panel renders its own chrome OUTSIDE the
      // generic side-panel, so it must be exempted explicitly — without
      // this, focusing a toolbox item closed the panel before the
      // insert fired, making the whole insert surface unusable.
      if (
        target.closest("[data-testid='side-panel']") ||
        target.closest("[data-testid='toolbox-mount']") ||
        target.closest("[data-activity-bar]")
      )
        return;
      setActivePanel(null);
    };

    globalThis.document.addEventListener("focusin", handleFocusIn);
    return () => globalThis.document.removeEventListener("focusin", handleFocusIn);
  }, [isPanelPinned, activePanel]);

  // Close panel on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && activePanel) {
        setActivePanel(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activePanel]);

  // Open the Insert mega menu (ribbon's Insert ▾). The left Toolbox insert
  // panel was removed once Insert became a mega menu — ⌘/ and the rail focus
  // event now just open the mega menu (InsertRibbonButton listens for this).
  const openInsertPanel = useCallback(() => {
    window.dispatchEvent(new CustomEvent("lilia:open-insert-menu"));
  }, []);

  // ⌘/ (and Ctrl+/) opens the Insert mega menu. Window-level so any focused
  // element (TipTap, inputs) yields.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (!isMeta || e.key !== "/") return;
      e.preventDefault();
      openInsertPanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openInsertPanel]);

  // Rail focus event (dispatched by other insert entry points) opens
  // the panel too — kept symmetric with the keyboard path.
  useEffect(() => {
    window.addEventListener(TOOLBOX_FOCUS_SEARCH_EVENT, openInsertPanel);
    return () => window.removeEventListener(TOOLBOX_FOCUS_SEARCH_EVENT, openInsertPanel);
  }, [openInsertPanel]);

  // Restoring a version replaces the document's blocks on the server. Cache
  // invalidation alone is not enough to show that: the editor store's
  // initDocument is intentionally once-per-doc-id, so the canvas keeps rendering
  // the pre-restore blocks until a full page reload — the rollback works and
  // looks like it did not. Same trap the "lilia:document-applied" listener below
  // already solves for Ask Lilia, and the same remedy: refetch, then adopt,
  // which resets the sync baseline the way conflict "takeTheirs" does.
  //
  // Creating a version does not change content, so it only needs the caches.
  const handleVersionChange = useCallback(async (contentChanged = false) => {
    queryClient.invalidateQueries({ queryKey: ["document", id] });
    queryClient.invalidateQueries({ queryKey: ["document-versions", id] });
    if (contentChanged) {
      try {
        const result = await refetch();
        if (result.data) {
          adoptServerDocument(result.data);
        }
      } catch {
        /* soft-fail — the Refresh chip still offers a hard reload */
      }
    }
    setPreviewRefreshKey((k) => k + 1);
  }, [queryClient, id, refetch, adoptServerDocument]);

  const handleDataChange = useCallback(() => {
    refetch();
  }, [refetch]);

  // Ask Lilia applied (or Undo / Refresh chip) → adopt server blocks into the
  // editor store. Previously we only invalidated the React Query cache; the
  // store's initDocument is intentionally once-per-doc-id, so the canvas stayed
  // stale until a full page reload. adoptServerDocument resets the sync
  // baseline the same way conflict "takeTheirs" does.
  useEffect(() => {
    const onApplied = async () => {
      queryClient.invalidateQueries({ queryKey: ["document", id] });
      try {
        const result = await refetch();
        if (result.data) {
          adoptServerDocument(result.data);
        }
      } catch {
        /* soft-fail — user can still hard-reload via Refresh chip */
      }
      setPreviewRefreshKey((k) => k + 1);
    };
    window.addEventListener("lilia:document-applied", onApplied);
    return () => window.removeEventListener("lilia:document-applied", onApplied);
  }, [queryClient, id, refetch, adoptServerDocument]);

  const handleSaveNow = useCallback(() => {
    // "Save" now means "cut a version" — durability is continuous.
    void createCheckpoint();
  }, [createCheckpoint]);

  const handleTitleChange = useCallback((newTitle: string) => {
    setTitle(newTitle);
  }, [setTitle]);

  // Publish the name to the shell bar, which renders and edits it but must
  // not import editorStore to do so — see openDocumentStore. Renaming still
  // runs through setTitle, so the Title block stays in lockstep and the
  // document is marked dirty exactly as before.
  const setOpenDocument = useOpenDocumentStore((s) => s.setOpenDocument);
  const clearOpenDocument = useOpenDocumentStore((s) => s.clearOpenDocument);
  // A viewer can't rename, and the shell bar names whose document it is.
  const ownerName = document?.ownerName || document?.ownerEmail || "";
  const ownerEmail = document?.ownerEmail ?? undefined;
  const sharedBy = useMemo(
    () => (document && !mayEdit ? { name: ownerName || "its owner", email: ownerEmail } : null),
    [document, mayEdit, ownerName, ownerEmail],
  );
  useEffect(() => {
    setOpenDocument(title ?? "", mayEdit ? handleTitleChange : null, sharedBy);
    return () => clearOpenDocument();
  }, [title, handleTitleChange, mayEdit, sharedBy, setOpenDocument, clearOpenDocument]);

  // ─── Command palette action registry ─────────────────────────────────────
  // Kept inline because every action closes over local state/handlers.
  // If this list grows, factor into useEditorCommands(...) hook.
  const [isDark, setIsDark] = useDarkMode();
  const docId = document?.id ?? "";
  const paletteActions: CommandAction[] = [
    // ── View
    // Mode labels per Claude Design round 2 note 2: match the topbar
    // segmented control names so the same concept has one name. The
    // topbar shows "Edit / Split / Preview";
    // here we surface the matching mode + a parenthetical so the
    // palette entry is recognizable from the topbar.
    { id: "view-visual", group: "View", label: "Edit (visual editor)", icon: <IconPencil size={14} />, run: () => handleModeChange("visual") },
    { id: "view-split", group: "View", label: "Edit + source (split view)", icon: <IconColumns size={14} />, run: () => handleModeChange("split"), shortcut: "⌘\\" },
    { id: "view-source", group: "View", label: "Source (LaTeX / Typst / LML)", icon: <IconCode size={14} />, run: () => handleModeChange("source") },
    { id: "view-toggle-pin", group: "View", label: isPanelPinned ? "Unpin side panel (overlay)" : "Pin side panel (push content)", icon: <IconColumns size={14} />, run: handleTogglePin },
    // Outline + Comments rails — re-housed from the old Review/View ribbon tabs
    // (Option C, Handoff 23) so they stay discoverable, not just keyboard-only.
    { id: "view-outline", group: "View", label: "Outline", icon: <IconListTree size={14} />, shortcut: shortcutLabel("outline"), run: () => window.dispatchEvent(new CustomEvent("lilia:open-outline-rail", { cancelable: true })) },
    { id: "view-comments", group: "View", label: "Comments", icon: <IconMessage size={14} />, shortcut: shortcutLabel("review"), run: () => window.dispatchEvent(new CustomEvent("lilia:open-comments-rail", { cancelable: true })) },
    // ── Navigate
    { id: "nav-studio", group: "Navigate", label: "Studio", icon: <IconLayoutCards size={14} />, run: () => navigate(`/studio/${docId}`, { state: { from: `/d/${docId}`, fromLabel: "Editor" } }), disabled: !docId },
    { id: "nav-focus", group: "Navigate", label: "Focus mode", icon: <IconFocus2 size={14} />, run: () => navigate(`/studio/${docId}?tab=focus`), disabled: !docId },
    { id: "nav-validate", group: "Navigate", label: "Validate document", icon: <IconCheck size={14} />, run: () => navigate(`/studio/${docId}?tab=validate`), disabled: !docId },
    { id: "nav-back", group: "Navigate", label: "Back to My Documents", icon: <IconArrowLeft size={14} />, run: () => navigate("/documents") },
    // ── Document
    { id: "doc-save", group: "Document", label: "Save now", icon: <IconDeviceFloppy size={14} />, shortcut: "⌘S", run: handleSaveNow, disabled: !isDirty },
    { id: "doc-paste-latex", group: "Document", label: "Paste LaTeX", icon: <IconFileImport size={14} />, run: () => setShowImportLatex(true) },
    { id: "doc-packages", group: "Document", label: "LaTeX packages", icon: <IconPackage size={14} />, hint: "Browse catalog · install · remove", run: () => setShowPackagesModal(true) },
    { id: "doc-feedback", group: "Document", label: "Send feedback", icon: <IconMessage size={14} />, hint: "Tell us what's working or broken", run: () => setShowFeedbackDialog(true) },
    // ── Theme
    {
      id: "theme-toggle",
      group: "Theme",
      label: isDark ? "Switch to light mode" : "Switch to dark mode",
      icon: isDark ? <IconSun size={14} /> : <IconMoon size={14} />,
      run: () => setIsDark(isDark ? "light" : "dark"),
    },
    // ── Insert (data-driven from /api/lilia/insertions; appended below)
  ];

  // Append data-driven insertion actions. Same registry as the side panel,
  // shared via the useInsertions hook. Tokens are grouped by package so the
  // palette mirrors the InsertionsPanel tree:
  //   • Insert · recent             ← user's last-used (bubbles to top)
  //   • Insert · kernel             ← LaTeX core (no package required)
  //   • Insert · {packageSlug}      ← one group per installed package
  // User-asked 2026-04-29: "make the tokens visible by package, kind of a
  // tree" in ⌘K. Sorting within each group is preserved from the API order.
  const { data: insertions = [] } = useInsertions(docId);
  const recentInsertions = useRecentInsertionTokens(insertions, 6);
  const recentKeys = new Set(recentInsertions.map(tokenKey));
  const insertActionFor = (ins: typeof insertions[number], groupName: string, idPrefix: string): CommandAction => ({
    id: `${idPrefix}-${tokenKey(ins)}`,
    group: groupName,
    label: ins.kind === "environment" || ins.expectsBody
      ? `\\begin{${ins.name}} … \\end{${ins.name}}`
      : `\\${ins.name}`,
    icon: ins.kind === "environment" || ins.expectsBody
      ? <IconBraces size={14} />
      : <IconWand size={14} />,
    hint: ins.notes ?? (ins.packageSlug ?? "kernel"),
    disabled: !activeEditor,
    run: () => insertTokenIntoEditor(ins, activeEditor, { source: "palette", documentId: docId || null }),
  });
  // Recents first so they're at the top with no query.
  for (const ins of recentInsertions) {
    paletteActions.push(insertActionFor(ins, "Insert · recent", "insert-recent"));
  }
  // Bucket the remaining tokens by package (kernel = null/empty slug).
  const buckets = new Map<string, typeof insertions>();
  for (const ins of insertions) {
    if (recentKeys.has(tokenKey(ins))) continue;
    const slug = ins.packageSlug || "kernel";
    const bucket = buckets.get(slug);
    if (bucket) bucket.push(ins);
    else buckets.set(slug, [ins]);
  }
  // Kernel first, then packages alphabetically — gives a stable tree.
  const orderedSlugs = Array.from(buckets.keys()).sort((a, b) => {
    if (a === "kernel") return -1;
    if (b === "kernel") return 1;
    return a.localeCompare(b);
  });
  for (const slug of orderedSlugs) {
    const groupName = slug === "kernel" ? "Insert · kernel" : `Insert · ${slug}`;
    for (const ins of buckets.get(slug)!) {
      paletteActions.push(insertActionFor(ins, groupName, "insert"));
    }
  }

  // ── Section lane (§ prefix) — wires the heading list into the palette
  // so users can jump to any heading by typing its name. Per Desktop
  // workflows.html scope-prefix contract.
  const sectionCounters: number[] = [];
  for (const block of blocks) {
    if (block.type !== "heading") continue;
    const level = Math.max(1, Math.min(6, (block.content?.level as number) || 1));
    const text = ((block.content?.text as string) || "").trim() || "Untitled";
    while (sectionCounters.length < level) sectionCounters.push(0);
    while (sectionCounters.length > level) sectionCounters.pop();
    sectionCounters[level - 1]++;
    for (let i = level; i < sectionCounters.length; i++) sectionCounters[i] = 0;
    const ordinal = sectionCounters.slice(0, level).filter((n) => n > 0).join(".");
    paletteActions.push({
      id: `section-${block.id}`,
      group: "Sections",
      scope: "section",
      label: `§${ordinal} ${text}`,
      icon: <IconArrowRight size={14} />,
      run: () => handleScrollToBlock(block.id),
    });
  }

  // ── Citation lane (@ prefix) — bib entries jump to the bibliography
  // panel and highlight the entry. Per Desktop workflows.html scope-
  // prefix contract.
  for (const entry of (document?.bibliography ?? [])) {
    const fields = (entry.data || entry.fields || {}) as Record<string, string>;
    const author = fields.author?.split(/\sand\s/)[0]?.replace(/\{|\}/g, "").trim() || "";
    const yearMatch = (fields.year || fields.date || "").match(/\d{4}/);
    const year = yearMatch ? yearMatch[0] : "";
    const titleField = (fields.title || "").replace(/\{|\}/g, "").trim();
    const titleShort = titleField.length > 60 ? titleField.slice(0, 60) + "…" : titleField;
    paletteActions.push({
      id: `citation-${entry.id}`,
      group: "Citations",
      scope: "citation",
      label: `@${entry.citeKey}`,
      hint: [author, year, titleShort].filter(Boolean).join(" · "),
      icon: <IconQuote size={14} />,
      run: () => {
        setActivePanel("bibliography");
        useEditorStore.getState().setHighlightedCiteKey(entry.citeKey);
      },
    });
  }

  const handleLatexImport = useCallback(
    async (parsed: { type: string; content: Record<string, unknown> }[], mode: "append" | "replace", bib?: string) => {
      if (!parsed.length) return;
      const current = useEditorStore.getState().blocks;
      const startIdx = mode === "replace" ? 0 : current.length;
      const mapped = parsed.map((p, i) => ({
        id: generateUUID(),
        type: p.type,
        content: p.content,
        sortOrder: startIdx + i,
      }));
      // The Flow canvas owns the document. setBlocks alone marks the store dirty,
      // and BlockEditor only re-renders external blocks while the store is NOT
      // dirty — so the import never reached the canvas, and the next save read the
      // unchanged canvas. Put the blocks into the editor; its onUpdate syncs the
      // store and schedules the save.
      const editor = useEditorStore.getState().editorInstance;
      if (editor && !editor.isDestroyed) {
        const nodes = mapped
          .map((b) => blockToTiptapNode(b as unknown as Block))
          .filter((n): n is JSONContent => n != null);
        const end = editor.state.doc.content.size;
        const atSlash = mode === "append" && importAtSlashRef.current ? takeSlashInsertRange(editor) : null;
        editor.chain().focus().insertContentAt(mode === "replace" ? { from: 0, to: end } : atSlash ?? end, nodes).run();
      } else {
        useEditorStore.getState().setBlocks(mode === "replace" ? mapped : [...current, ...mapped]);
      }

      // If the user also pasted a .bib file, import it into the document's bibliography.
      // This covers the most common LaTeX workflow: \bibliography{refs.bib} where the
      // BibTeX entries live in a separate .bib file (50-60% of academic papers).
      if (bib && id) {
        try {
          await fetchWithAuth(`/api/documents/${id}/bibliography/import`, {
            method: "POST",
            body: JSON.stringify({ bibTexContent: bib }),
          });
          handleDataChange(); // refresh bibliography panel
        } catch (err) {
          console.error("[EditorPage] BibTeX import failed:", err);
        }
      }
    },
    [id, fetchWithAuth, handleDataChange],
  );

  // Default the import modal to "replace" when the doc is effectively empty
  // (one paragraph block with no text). Otherwise default to "append" so we
  // never accidentally clobber existing work.
  const isEffectivelyEmpty =
    blocks.length === 0 ||
    (blocks.length === 1 &&
      blocks[0].type === "paragraph" &&
      !((blocks[0].content as { text?: string })?.text?.trim()));

  const handleScrollToBlock = useCallback((blockId: string) => {
    // Update focused block so LaTeX/LML highlight follows (and left canvas
    // paints data-block-focused). Prefer the Flow canvas so we don't scroll
    // a duplicate id in outline / other rails.
    useEditorStore.getState().setFocusedBlockId(blockId);
    const root =
      globalThis.document.querySelector('[data-testid="flow-editor"]') ??
      globalThis.document;
    const el = root.querySelector(`[data-block-id="${blockId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handleSettingsSave = useCallback((settings: {
    columns?: number;
    columnGap?: number;
    columnSeparator?: string;
    paperSize?: string;
    fontFamily?: string;
    fontSize?: number;
    experimentalLatexEdit?: boolean;
    customPreamble?: string;
    look?: DocumentLook;
    headingNumbering?: number;
  }) => {
    if (settings.columns !== undefined) setDocColumns(settings.columns);
    if (settings.columnGap !== undefined) setDocColumnGap(settings.columnGap);
    if (settings.columnSeparator !== undefined) setDocColumnSeparator(settings.columnSeparator);
    if (id) {
      // The look applies at once (canvas headings, outline chips); a refusal
      // from the API puts the previous one back and says why.
      const key = ["document", id];
      const previousLook = queryClient.getQueryData<DocumentWithBlocks>(key)?.look ?? null;
      if (settings.look) {
        queryClient.setQueryData<DocumentWithBlocks>(key, (d) => (d ? { ...d, look: settings.look } : d));
      }
      api.documents.update(id, settings).then(({ document: saved }) => {
        // Keep the editor's copy of the settings in step, so the dialog reopens on what was saved.
        if (saved) queryClient.setQueryData<DocumentWithBlocks>(key, (d) => (d ? { ...d, ...saved, blocks: d.blocks } : d));
      }).catch((err) => {
        console.error("Failed to save document settings:", err);
        if (settings.look) {
          queryClient.setQueryData<DocumentWithBlocks>(key, (d) => (d ? { ...d, look: previousLook } : d));
          showToast(err instanceof Error && err.message ? err.message : "The look couldn't be saved.", "error");
        }
      });
    }
  }, [id, queryClient, showToast]);

  // Bibliography handlers
  const handleAddBibEntry = useCallback(async (entry: { citeKey: string; entryType: string; fields: Record<string, string> }) => {
    const response = await fetchWithAuth(`/api/documents/${id}/bibliography`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ citeKey: entry.citeKey, entryType: entry.entryType, data: entry.fields }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || "Failed to add entry");
    }
    handleDataChange();
  }, [id, handleDataChange, fetchWithAuth]);

  const handleUpdateBibEntry = useCallback(async (entryId: string, entry: Partial<{ citeKey: string; entryType: string; fields: Record<string, string> }>) => {
    const response = await fetchWithAuth(`/api/documents/${id}/bibliography/${entryId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ citeKey: entry.citeKey, entryType: entry.entryType, data: entry.fields }),
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || "Failed to update entry");
    }
    handleDataChange();
  }, [id, handleDataChange, fetchWithAuth]);

  const handleDeleteBibEntry = useCallback(async (entryId: string) => {
    const response = await fetchWithAuth(`/api/documents/${id}/bibliography/${entryId}`, { method: "DELETE" });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || "Failed to delete entry");
    }
    handleDataChange();
  }, [id, handleDataChange, fetchWithAuth]);

  const handleImportBibtex = useCallback(async (bibtex: string, overwrite: boolean) => {
    const result = await importBibtex(fetchWithAuth, id ?? "", bibtex, overwrite,
      (document?.bibliography ?? []).map((b: { citeKey: string }) => b.citeKey));
    handleDataChange();
    return result;
  }, [id, document?.bibliography, handleDataChange, fetchWithAuth]);

  const handleDeleteLabel = useCallback(async (labelId: string) => {
    const response = await fetchWithAuth(`/api/documents/${id}/labels/${labelId}`, { method: "DELETE" });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.message || "Failed to delete label");
    }
    handleDataChange();
  }, [id, handleDataChange, fetchWithAuth]);

  const inWorkspace = !!workspaceShell;
  const shellH = inWorkspace ? "h-full w-full" : "h-screen w-screen";

  // Must run before any early return (hooks rules).
  // Report while loading so shared chrome + top loading bar stay driven
  // without waiting for the full document payload.
  useReportWorkspaceTopBar(inWorkspace, document ? {
    title,
    onTitleChange: handleTitleChange,
    mode: editorMode === "source" ? "source" : editorMode === "split" ? "split" : "visual",
    onModeChange: handleModeChange,
    saveStatus: autoSaveStatus,
    isDirty,
    onSaveNow: handleSaveNow,
    lastSavedAt,
    isPublic: document.isPublic,
    shareLink: document.shareLink,
    shareSlug: document.shareSlug,
    ownerId: document.ownerId,
    presenceUsers,
    onPasteLatex: () => setShowImportLatex(true),
    onOpenPackages: () => setShowPackagesModal(true),
    onOpenSettings: () => setShowSettingsDialog(true),
    document,
    documentLoading: isLoading,
    documentReadOnly: !mayEdit,
    // The top bar's "N issues" badge: the last check's error and warnings. Never passed before,
    // so the badge never showed on a document (16 Oct).
    validationIssueCount: flowValidation ? (flowValidation.valid === false ? 1 : 0) + (flowValidation.warnings?.length ?? 0) : 0,
  } : {
    title: "",
    onTitleChange: () => {},
    saveStatus: "idle",
    isDirty: false,
    onSaveNow: () => {},
    documentLoading: true,
  });

  // Phones get the mobile reader (Olivia, design-handoffs/2026-10-10-mobile-reader).
  const mobileReader = useMobileReader();
  // Check document on a phone: runs on open and after changes (no blocks, no run).
  // Just created on a phone (?start=ask): Ask Lilia opens with starter asks. Read once, then tidied.
  const [startAsk] = useState(() => new URLSearchParams(window.location.search).get("start") === "ask");
  useEffect(() => {
    if (!startAsk) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("start");
    window.history.replaceState(window.history.state, "", url.toString());
  }, [startAsk]);
  const mobileCheck = useDocumentCheck(id ?? "", mobileReader && !isLoading ? blocks : NO_BLOCKS, fetchWithAuth);
  // The publisher check on a phone (25 Oct 2026): with the document check, for a document with a venue.
  const venueId = (document as { publisherProfile?: string | null } | undefined)?.publisherProfile ?? null;
  const { profiles: venueProfiles } = usePublisherProfiles({ enabled: mobileReader && !!venueId });
  const venueCheck = usePublisherCheck(id, mobileReader && !!venueId);
  const mobilePublisherCheck = venueId && venueCheck.data
    ? { publisher: venueProfiles.find((p) => p.id === venueId)?.publisher ?? "", status: venueCheck.data.status, findings: venueCheck.data.findings }
    : null;
  // Look on a phone: the themes, and the saved look while one is being previewed.
  const { themes: mobileThemes } = useThemes({ enabled: mobileReader });
  const savedLook = useRef<{ look: DocumentLook | null } | null>(null);

  // Loading / error states
  if (isLoading) {
    // ds30 §9d: a Charter-measure skeleton (title bar + text lines shimmering),
    // never a bare spinner. Bars pulse at --motion-slow.
    return (
      <div
        data-testid="doc-loading"
        aria-busy="true"
        aria-label={i18n.t("editor:ui.loadingDocument")}
        className={cn("flex justify-center overflow-hidden bg-[var(--lilia-bg)] pt-24", shellH)}
      >
        <div className="w-full max-w-[var(--lilia-doc-measure,640px)] px-6">
          <div className="ai-pulse h-8 w-3/4 rounded bg-[var(--lilia-bg-muted)]" style={{ animationDuration: "240ms" }} />
          <div className="mt-6 space-y-3.5">
            {[92, 100, 85, 96, 70, 88].map((w, i) => (
              <div
                key={i}
                className="ai-pulse h-3.5 rounded bg-[var(--lilia-bg-muted)]"
                style={{ width: `${w}%`, opacity: 0.7 }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div data-testid="doc-not-found" className={cn("flex flex-col items-center justify-center text-center text-[var(--lilia-text-muted)] bg-[var(--lilia-bg)]", shellH)}>
        <h2 className="text-[var(--lilia-text)] mb-2">{i18n.t("editor:ui.notFound")}</h2>
        <p>{i18n.t("editor:ui.notFoundBody")}</p>
      </div>
    );
  }

  // Split = visual editor + source pane beside it (source replaces the old
  // "preview" secondary). Full "source" mode = source alone in the main column.
  // Never open the secondary pane while chat is Primary (would be 3 columns);
  // handleModeChange demotes chat to Side first.
  const showSourceBeside = editorMode === "split" && !isChatLayout;
  const showSourceTabs = false;

  if (mobileReader) {
    return (
      <MobileReader
        title={title}
        viewOnly={!mayEdit}
        ownerLabel={document.ownerName ?? undefined}
        blocks={blocks}
        body={<EditorPanel mode="visual" showSourceTabs={false} sourceRefreshKey={previewRefreshKey} look={canvasLook} />}
        renderOutline={(go) => <OutlinePanel blocks={blocks} onScrollToBlock={go} chapterColours={canvasLook?.headingColours} />}
        renderPreview={({ seeIssues }) => (
          <MobilePreview fetchWithAuth={fetchWithAuth} pdfUrl={`${API_BASE}/documents/${document.id}/export/pdf`} refreshKey={previewRefreshKey} onSeeIssues={seeIssues} />
        )}
        check={mobileCheck}
        publisherCheck={mobilePublisherCheck}
        askOnArrival={startAsk}
        renderLatex={({ highlightBlockId, tab, onTab }) => (
          <MobileLatexView fetchWithAuth={fetchWithAuth} documentId={document.id} refreshKey={previewRefreshKey} highlightBlockId={highlightBlockId} blocks={blocks} tab={tab} onTabChange={onTab} />
        )}
        renderAsk={({ context, clearContext, close, prefill, autoFocus = true }) => (
          <AskLiliaPanel
            initialMessage={prefill}
            documentId={id ?? undefined}
            onClose={close}
            onApplyLatex={applyLatexFromChat}
            primarySurface
            syncEnabled
            blockContext={context}
            onClearBlockContext={clearContext}
            autoFocusInput={autoFocus}
            phoneSheet
          />
        )}
        onBack={() => navigate("/")}
        onScrollToBlock={handleScrollToBlock}
        documentId={document.id}
        bibliography={(document.bibliography || []).map((b) => ({ citeKey: b.citeKey, fields: (b.data || b.fields || {}) as Record<string, string> }))}
        share={{
          // Managing sharing is the owner's (as on desktop); everyone else gets Copy link and the people list.
          canManage: (document.role ?? "owner") === "owner",
          ownerId: document.ownerId,
          ownerName: document.ownerName ?? undefined,
          initial: {
            isPublic: !!document.isPublic,
            shareLink: document.shareLink ?? "",
            shareSlug: document.shareSlug ?? "",
            linkPermission: (document as { linkPermission?: string }).linkPermission ?? "view",
            linkExpiresAt: (document as { linkExpiresAt?: string | null }).linkExpiresAt ?? null,
          },
        }}
        details={{ owner: document.ownerName ?? undefined, updatedAt: document.updatedAt }}
        renderSettings={() => {
          const d = document as typeof document & Partial<DocSettingsValues> & { latexDocumentClassOptions?: string | null; latexDocumentClass?: string | null };
          return (
            <MobileSettingsSheet
              values={{ ...d, title, documentClass: d.latexDocumentClass, classOptions: d.latexDocumentClassOptions }}
              canEdit={mayEdit}
              typeLabel={computeTypeLabel(document as unknown as StampDocumentInput)}
              publisherProfile={document.publisherProfile}
              isOwner={isOwn(document.role)}
              onPublisher={async (publisherProfile) => {
                await api.documents.update(document.id, { publisherProfile });
                await queryClient.invalidateQueries({ queryKey: ["document", document.id] });
                setPreviewRefreshKey((k) => k + 1);
              }}
              onRename={async (t) => handleTitleChange(t)}
              onChange={async (patch: DocSettingsPatch) => {
                const key = ["document", document.id];
                const before = queryClient.getQueryData<DocumentWithBlocks>(key);
                queryClient.setQueryData<DocumentWithBlocks>(key, (x) => (x ? ({ ...x, ...patch } as DocumentWithBlocks) : x));
                try {
                  await api.documents.update(document.id, patch as Parameters<typeof api.documents.update>[1]);
                  setPreviewRefreshKey((k) => k + 1);
                } catch (err) {
                  queryClient.setQueryData(key, before);
                  throw err;
                }
              }}
            />
          );
        }}
        renderLook={({ close }) => {
          const key = ["document", document.id];
          const current = effectiveTheme(document.look, document.lookLocked, document.lookThemes);
          const restore = () => {
            if (savedLook.current) queryClient.setQueryData<DocumentWithBlocks>(key, (x) => (x ? { ...x, look: savedLook.current!.look } : x));
            savedLook.current = null;
          };
          return (
            <MobileLookSheet
              themes={mobileThemes}
              current={savedLook.current ? effectiveTheme(savedLook.current.look, document.lookLocked, document.lookThemes) : current}
              locked={document.lookLocked}
              allowed={document.lookThemes}
              canEdit={mayEdit}
              onPreview={(theme) => {
                if (!savedLook.current) savedLook.current = { look: document.look ?? null };
                const base = normaliseLook(savedLook.current.look);
                queryClient.setQueryData<DocumentWithBlocks>(key, (x) => (x ? { ...x, look: { ...base, theme } } : x));
              }}
              onApply={async (theme) => {
                const base = normaliseLook(savedLook.current ? savedLook.current.look : document.look);
                const look = { ...base, theme };
                try {
                  await api.documents.update(document.id, { look });
                } catch (err) {
                  restore();
                  throw err;
                }
                queryClient.setQueryData<DocumentWithBlocks>(key, (x) => (x ? { ...x, look } : x));
                savedLook.current = null;
                setPreviewRefreshKey((k) => k + 1);
                close();
              }}
              onCancel={() => {
                restore();
                close();
              }}
            />
          );
        }}
        onLookDismiss={() => {
          if (savedLook.current) {
            const look = savedLook.current.look;
            queryClient.setQueryData<DocumentWithBlocks>(["document", document.id], (x) => (x ? { ...x, look } : x));
            savedLook.current = null;
          }
        }}
        onUndoAll={async (versionId) => {
          await api.documents.versions.restore(document.id, versionId);
          window.dispatchEvent(new CustomEvent("lilia:document-applied", { detail: { documentId: document.id, changedBlockIds: [] } }));
        }}
        onUndoChange={async (change) => {
          if (change.kind === "added") await api.blocks.delete(document.id, change.blockId);
          else if (change.kind === "changed" && change.before)
            await api.blocks.update(document.id, change.blockId, { type: change.before.type, content: change.before.content });
          window.dispatchEvent(new CustomEvent("lilia:document-applied", { detail: { documentId: document.id, changedBlockIds: [] } }));
        }}
      />
    );
  }

  const panelTitles: Record<string, string> = {
    outline: "Outline",
    bibliography: "Bibliography",
    formulas: "Formulas",
    elements: "Elements",
    insertions: "Insertions",
    drafts: "Drafts",
    labels: "Labels & references",
    comments: "Comments",
    history: "History",
  };

  return (
    <div className={cn("editor-shell flex flex-col overflow-hidden bg-[var(--lilia-bg)]", shellH)}>
      {/* Standalone only — workspace layout owns LiliaTopBar + TopBar. */}
      {!inWorkspace && (
        <>
          {/* One chrome everywhere. This branch used to render the
              pre-Compact two-row bar, which kept a duplicate search, a
              duplicate theme toggle, a Word button that also sits under the
              Export caret, and two control outlines drawn on the 1.22:1
              legacy border token. It hosts the same compact cluster the
              workspace layout does. */}
          <LiliaTopBar
            onOpenPalette={() => setPaletteOpen(true)}
            titleAccessory={<SaveIndicator />}
            documentSlot={
              <TopBar
                compact
                documentId={document.id}
                title={title}
                onTitleChange={handleTitleChange}
                mode={editorMode}
                onModeChange={handleModeChange}
                saveStatus={autoSaveStatus}
                isDirty={isDirty}
                onSaveNow={handleSaveNow}
                lastSavedAt={lastSavedAt}
                isPublic={document.isPublic}
                shareLink={document.shareLink}
                shareSlug={document.shareSlug}
                ownerId={document.ownerId}
                presenceUsers={presenceUsers}
                onPasteLatex={() => setShowImportLatex(true)}
                blockCount={blocks.length}
                onOpenCommandPalette={() => setPaletteOpen(true)}
                onOpenPackages={() => setShowPackagesModal(true)}
                onOpenSettings={() => setShowSettingsDialog(true)}
                document={document}
                documentReadOnly={!mayEdit}
                arrangement={arrangement}
                onArrangementChange={setArrangement}
              />
            }
          />
        </>
      )}


      {/* Full-width formatting ribbon removed (trial 2026-07-18).
          Flow mega menus (Style · Format · Insert) float over the canvas
          only — see flow mega strip inside the main row below. ⌘. no-ops
          on the old collapse; menus stay visible on Flow. */}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} actions={paletteActions} />

      <PackagesModal
        open={showPackagesModal}
        onClose={() => setShowPackagesModal(false)}
        documentId={document.id}
      />

      <ImportLatexModal
        open={showImportLatex}
        onClose={() => {
          setShowImportLatex(false);
          importAtSlashRef.current = false;
        }}
        onImport={handleLatexImport}
        fetchWithAuth={fetchWithAuth}
        defaultMode={isEffectivelyEmpty ? "replace" : "append"}
      />

      {/* "Existing table…" from the Insert menu or the slash menu: one of the
          author's tables, linked or copied into this paper through the editor. */}
      <EditorTablePicker
        documentId={document.id}
        fetchWithAuth={fetchWithAuth}
        getEditor={() => useEditorStore.getState().editorInstance}
      />

      {/* Permanent format strip + selection bubble menu are both off on Flow.
          Inline format: Home ribbon (when present), ⌘K, and keyboard
          shortcuts (⌘B/I/U). Do NOT re-add a permanent strip or selection
          floating toolbar without an explicit product call. */}

      <div className="flex-1 flex overflow-hidden relative">
        {/* ds24 Arrangement A — single left utility rail. Structure cluster
            (Outline / References / Symbols) on top, review cluster (Comments
            / Validate / History) pinned to the bottom. One 258px panel open
            at a time; source + preview + Ask Lilia stay on the right. */}
        <PanelRail
          active={activeRailTab}
          onChange={setRailTab}
          commentsWarn={(commentCount?.unresolved ?? 0) > 0}
          validateWarn={railValidateWarn}
          count={(p) => {
            if (p === "outline") return `${blocks.length} blocks`;
            if (p === "refs") {
              const n = document.bibliography?.length ?? 0;
              return n === 1 ? "1 entry" : `${n} entries`;
            }
            return undefined;
          }}
          outline={<OutlinePanel blocks={blocks} onScrollToBlock={handleScrollToBlock} chapterColours={canvasLook?.headingColours} />}
          refs={
            <BibliographyPanel
              documentId={document.id}
              entries={(document.bibliography || []).map((b) => ({
                id: b.id,
                citeKey: b.citeKey,
                entryType: b.entryType,
                fields: (b.data || b.fields || {}) as Record<string, string>,
              }))}
              onAdd={handleAddBibEntry}
              onUpdate={handleUpdateBibEntry}
              onDelete={handleDeleteBibEntry}
              onImport={handleImportBibtex}
              onInsertCitation={insertCitation}
            />
          }
          xrefs={
            <ReferencesPanel
              documentId={document.id}
              fetchWithAuth={fetchWithAuth}
              onGoToBlock={handleScrollToBlock}
              onReferenceHere={(t) => {
                // The picker's mirror: the same command, at the caret.
                activeEditor?.chain().focus().insertReference(t.key, formFor(t)).insertContent(" ").run();
              }}
              refreshKey={lastSavedAt}
            />
          }
          syms={<SymbolsPanel query={symbolsQuery} onInsert={(latex) => insertInlineMath(latex)} />}
          review={<CommentsPanel documentId={document.id} blocks={blocks} onScrollToBlock={handleScrollToBlock} />}
          validate={
            <ValidateRailPanel
              documentValidation={flowValidation}
              stale={
                validatedSignature !== null &&
                validatedSignature !== validationSignature(blocks)
              }
              saveStatus={autoSaveStatus}
              blockCount={blocks.length}
              onValidate={runFlowValidation}
              isValidating={isValidatingFlow}
              onOpenStudio={() => navigate(`/d/${docId}/validate`)}
            />
          }
          history={
            <VersionHistoryPanel
              documentId={document.id}
              onVersionRestored={() => {
                // The panel deliberately stays open. It used to close itself here,
                // which is why restoring read as "everything disappeared" and why the
                // restored version never appeared selected — the list it would have
                // been selected in was gone. Leaving it open shows the refreshed
                // history with the restore recorded at the top, which is the
                // confirmation the action was missing.
                void handleVersionChange(true);   // content changed on the server
              }}
              onVersionCreated={() => void handleVersionChange(false)}
            />
          }
        />

        {/* Left Toolbox insert panel removed — Insert is now the ribbon mega
            menu (Insert ▾); ⌘/ opens it. SidePanel + ActivityBar were folded
            into the PanelRail (ds24). Block-mode toolbox lives on /studio. */}

        {isChatLayout ? (
          /*
            The workspace, as one grid rather than two nested columns.
            ───────────────────────────────────────────────────────────────
                       col 1              col 2         col 3
            row 1   left header          handle      right header    48px
            row 2   left body            handle      right body      1fr

            Every pane's chrome and body is a direct child with an assigned
            column and row. Nothing is nested inside anything else, which is
            what lets a surface move from one pane to the other as a change of
            `gridColumn` and nothing more.

            That matters because a remount loses the selection, the undo
            history and the scroll position. Measured rather than assumed —
            spike/pane-move and web #261.

            Both panes offer all four surfaces, so anything can be on either
            side. Ask Lilia and the editor are mounted for the life of the page
            and hidden with `display: none` when they are on neither, never
            unmounted, for the same reason.
          */
            <div
              data-testid={testIds.chatFirst.workspaceGrid}
              data-left={panes.left}
              data-right={artifactOpen ? panes.right : "collapsed"}
              className="flex-1 min-w-0 grid overflow-hidden"
              style={{
                gridTemplateColumns: artifactOpen
                  ? `minmax(${CHAT_MIN}px, 1fr) auto ${artifactWidth}px`
                  : `minmax(${CHAT_MIN}px, 1fr) 0 ${REOPEN_RAIL_W}px`,
                gridTemplateRows: `${CHAT_FIRST_HEADER_H}px minmax(0, 1fr)`,
              }}
            >
              {/* ── Ask Lilia — mounted for the life of the page ──
                  It brings its own 48px header, so it spans both rows of
                  whichever column it is in. Hidden rather than unmounted:
                  coming back to a conversation that lost its half-typed
                  message would make moving it cost something, and it is meant
                  to cost nothing. */}
              <div
                data-testid={testIds.chatFirst.chatPrimaryColumn}
                className="flex flex-col overflow-hidden min-w-0 bg-[var(--lilia-ask-ground,#f7f9fc)]"
                style={{
                  gridColumn: paneOf("ask") === "left" ? 1 : 3,
                  gridRow: "1 / -1",
                  display: paneOf("ask") ? "flex" : "none",
                  ...(paneOf("ask") === "right" ? SEAM : {}),
                }}
              >
                <AskLiliaPanel
                  documentId={id ?? undefined}
                  onApplyLatex={applyLatexFromChat}
                  onClose={() => setArrangement("side")}
                  onMove={(how) => void moveAsk(how)}
                  arrangement="chat"
                  onArrange={setArrangement}
                  primarySurface
                  onMoveToSide={() => setArrangement("side")}
                  syncEnabled
                  headerTabs={
                    paneOf("ask") ? (
                      <PaneSurfaceTabs
                        side={paneOf("ask")!}
                        value="ask"
                        onChange={(next) => choose(paneOf("ask")!, next)}
                      />
                    ) : undefined
                  }
                />
              </div>

              {/* ── A header for each pane that is not showing Ask Lilia ──
                  Ask Lilia supplies its own; everything else needs one to put
                  the strip in. The document title and the collapse control
                  belong to the right pane, so they appear once and only there. */}
              {(["left", "right"] as const).map((side) => {
                const surface = panes[side];
                if (surface === "ask") return null;
                if (side === "right" && !artifactOpen) return null;
                return (
                  <div
                    key={side}
                    data-testid={testIds.chatFirst.paneHeader}
                    data-side={side}
                    data-surface={surface}
                    className="flex items-center gap-2.5 border-b border-[var(--lilia-border,#e4e9f1)] bg-[var(--lilia-bg,#f7f9fc)] pl-4 pr-2"
                    style={{
                      gridColumn: side === "left" ? 1 : 3,
                      gridRow: 1,
                      ...(side === "right" ? SEAM : {}),
                    }}
                  >
                    {/* The pane's own strip: what is true of THIS surface on
                        the left, what you could switch to on the right.

                        It used to carry the document's name — on both panes,
                        so a split view showed the same title twice while the
                        shell bar above showed it a third time. The name now
                        lives centred in the shell bar, which is the one place
                        it is unambiguous.

                        Left empty rather than refilled: the space is
                        surface-specific (a conversation switcher for Ask
                        Lilia, page N of M for PDF, the format for Source),
                        and guessing at that is how a strip becomes a junk
                        drawer. The spacer stays so the tabs keep their
                        position — they are muscle memory, and contextual
                        content must grow leftward from them, never push
                        them. */}
                    <span
                      data-testid={testIds.chatFirst.paneContext}
                      className="min-w-0 flex-1"
                    />
                    {side === "right" && (
                      <span
                        data-testid={testIds.chatFirst.pinnedBadge}
                        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-[10.5px] font-semibold text-[var(--lilia-primary-mild,#1C93C8)]"
                        style={{ background: "var(--lilia-primary-subtle,#0A76A414)" }}
                        title={i18n.t("editor:ui.staysOpen")}
                      >
                        {i18n.t("editor:ui.live")}
                      </span>
                    )}
                    <PaneSurfaceTabs
                      side={side}
                      value={surface}
                      onChange={(next) => choose(side, next)}
                    />
                    {side === "right" && (
                      <button
                        type="button"
                        data-testid={testIds.chatFirst.artifactClose}
                        onClick={closeArtifact}
                        title={i18n.t("editor:ui.collapsePane")}
                        aria-label={i18n.t("editor:ui.collapsePane")}
                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] text-[var(--lilia-text-secondary,#5a6a7e)] hover:bg-[var(--lilia-hover,#eaf1fb)]"
                      >
                        <IconX size={15} />
                      </button>
                    )}
                  </div>
                );
              })}

              {/* ── The editor. One instance, for the life of the page. ──
                  Rendered unconditionally and placed by style: a conditional
                  would put it at a different position in the tree and remount
                  it, which costs the selection, the undo history and the scroll
                  position. Only gridColumn and display change. Measured — see
                  spike/pane-move and web #261. */}
              <div
                data-testid={testIds.chatFirst.editorColumn}
                // The TikZ split view opens over this column (the workspace).
                data-tikz-sheet-host=""
                className="relative flex flex-col overflow-hidden min-w-0 min-h-0 bg-[var(--lilia-bg,#f7f9fc)]"
                style={{
                  gridColumn: paneOf("edit") === "left" ? 1 : 3,
                  gridRow: 2,
                  display: paneOf("edit") ? "flex" : "none",
                  ...(paneOf("edit") === "right" ? SEAM : {}),
                }}
              >
                <EditorToolsStrip
                  enabled={mayEdit}
                  readOnly={!mayEdit}
                  editor={activeEditor ?? null}
                  onDeleteBlock={() => {
                    const fid = useEditorStore.getState().focusedBlockId;
                    if (fid) useEditorStore.getState().deleteBlock(fid);
                  }}
                />
                <div className="flex-1 flex overflow-hidden min-h-0 min-w-0">
                  <EditorPanel
                    mode="visual"
                    showSourceTabs={showSourceTabs}
                    sourceRefreshKey={previewRefreshKey}
                    look={canvasLook}
                  />
                </div>
              </div>

              {/* ── Output. PDF and Source are the same panel on different
                  tabs, and both panes can hold one — the PDF next to its own
                  LaTeX is a real thing to want. Each mount offers only the tabs
                  its own surface stands for, so neither re-offers the other. */}
              {(["pdf", "source"] as const).map((surface) => {
                const side = paneOf(surface);
                if (!side) return null;
                return (
                  <div
                    key={surface}
                    className="flex overflow-hidden min-h-0 min-w-0 bg-[var(--lilia-bg,#f7f9fc)]"
                    style={{
                      gridColumn: side === "left" ? 1 : 3,
                      gridRow: 2,
                      ...(side === "right" ? SEAM : {}),
                    }}
                  >
                    <PreviewPanel
                      documentId={document.id}
                      tabs={surface === "pdf" ? PDF_ONLY : MARKUP_TABS}
                      activeTab={surface === "pdf" ? "pdf" : previewTab === "pdf" ? "latex" : previewTab}
                      documentTitle={title}
                      isVisible={true}
                      fill
                      onClose={() => choose(side, "edit")}
                      refreshKey={previewRefreshKey}
                      blocks={blocks}
                      bibliographyEntries={(document.bibliography || []).map((b) => ({
                        id: b.id,
                        citeKey: b.citeKey,
                        entryType: b.entryType,
                        fields: (b.data || b.fields || {}) as Record<string, string>,
                      }))}
                      onTabChange={handleTabChange}
                      focusedBlockId={focusedBlockId}
                      onScrollToBlock={handleScrollToBlock}
                    />
                  </div>
                );
              })}

              {artifactOpen ? (
                <div
                  data-testid={testIds.chatFirst.resizeHandle}
                  className="flex"
                  style={{ gridColumn: 2, gridRow: "1 / -1" }}
                >
                  <ResizeHandle onResize={onArtifactResize} direction="horizontal" />
                </div>
              ) : (
                <button
                  type="button"
                  data-testid={testIds.chatFirst.artifactReopen}
                  onClick={openArtifact}
                  title={i18n.t("editor:ui.showSecondPane")}
                  aria-label={i18n.t("editor:ui.showSecondPane")}
                  className="flex flex-col items-center justify-center gap-2 border-l border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-surface)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
                  style={{ gridColumn: 3, gridRow: "1 / -1" }}
                >
                  <IconListTree size={16} className="text-[var(--lilia-text-secondary)]" />
                  <span
                    style={{ writingMode: "vertical-rl" }}
                    className="text-[10.5px] font-semibold tracking-wide text-[var(--lilia-text-secondary)] max-h-[40vh] overflow-hidden text-ellipsis"
                  >
                    {title || "Document"}
                  </span>
                </button>
              )}
            </div>
        ) : (
          /* ── Side (classic): document primary + Ask Lilia docked right ── */
          <>
            <div
              // The workspace: the TikZ split view opens over it, leaving the
              // top bar and the Ask Lilia dock where they are.
              data-tikz-sheet-host=""
              className={`relative flex-1 flex overflow-hidden min-w-0 bg-[var(--lilia-bg)] ${showSourceBeside && effectivePreviewPosition === "bottom" ? "flex-col" : ""}`}
            >
              <div
                className={`${showSourceBeside && effectivePreviewPosition === "bottom" ? "flex-1 overflow-hidden min-h-0 flex flex-col" : "flex-1 flex flex-col overflow-hidden min-w-0"}`}
              >
                {/* The document's views, at the right of the row that is
                    already there. Ask Lilia is not among them: in this
                    arrangement it is docked at the right edge, so a tab for it
                    would move a panel that is already on screen. */}
                <EditorToolsStrip
                  enabled={mayEdit && sideSurface === "edit"}
                  readOnly={!mayEdit}
                  editor={activeEditor ?? null}
                  onDeleteBlock={() => {
                    const fid = useEditorStore.getState().focusedBlockId;
                    if (fid) useEditorStore.getState().deleteBlock(fid);
                  }}
                  trailing={
                    <DocumentSurfaceTabs value={sideSurface} onChange={setSideSurface} />
                  }
                />
                <div className="flex-1 min-h-0 overflow-hidden flex">
                  {sideSurface === "edit" ? (
                    <EditorPanel
                      // In split mode the secondary pane is source; keep the
                      // main column as the visual editor (not full SourceEditor).
                      mode={editorMode === "source" ? "source" : "visual"}
                      showSourceTabs={showSourceTabs}
                      sourceRefreshKey={previewRefreshKey}
                      look={canvasLook}
                    />
                  ) : (
                    <PreviewPanel
                      documentId={document.id}
                      tabs={sideSurface === "pdf" ? PDF_ONLY : MARKUP_TABS}
                      activeTab={sideSurface === "pdf" ? "pdf" : previewTab === "pdf" ? "latex" : previewTab}
                      documentTitle={title}
                      isVisible={true}
                      fill
                      onClose={() => setSideSurface("edit")}
                      refreshKey={previewRefreshKey}
                      blocks={blocks}
                      bibliographyEntries={(document.bibliography || []).map((b) => ({
                        id: b.id,
                        citeKey: b.citeKey,
                        entryType: b.entryType,
                        fields: (b.data || b.fields || {}) as Record<string, string>,
                      }))}
                      onTabChange={handleTabChange}
                      focusedBlockId={focusedBlockId}
                      onScrollToBlock={handleScrollToBlock}
                    />
                  )}
                </div>
              </div>

              {/* Source beside document — full PreviewPanel (LaTeX · LML · PDF)
                  in the old preview slot. Keeps preamble fold, block↔line sync,
                  density toggle, tabs. Not a bare SourceEditor. */}
              {showSourceBeside && (
                <>
                  <ResizeHandle
                    onResize={effectivePreviewPosition === "bottom" ? handlePreviewPanelResizeVertical : handlePreviewPanelResize}
                    direction={effectivePreviewPosition === "bottom" ? "vertical" : "horizontal"}
                  />
                  <PreviewPanel
                    documentId={document.id}
                    documentTitle={title}
                    isVisible={true}
                    onClose={() => handleModeChange("visual")}
                    refreshKey={previewRefreshKey}
                    blocks={blocks}
                    width={effectivePreviewPosition === "right" ? (previewPanelWidth ?? undefined) : undefined}
                    height={effectivePreviewPosition === "bottom" ? (previewPanelHeight ?? undefined) : undefined}
                    bibliographyEntries={(document.bibliography || []).map((b) => ({
                      id: b.id,
                      citeKey: b.citeKey,
                      entryType: b.entryType,
                      fields: (b.data || b.fields || {}) as Record<string, string>,
                    }))}
                    activeTab={previewTab}
                    onTabChange={handleTabChange}
                    focusedBlockId={focusedBlockId}
                    onScrollToBlock={handleScrollToBlock}
                  />
                </>
              )}
            </div>

            {/* Ask Lilia — IN-FLOW right column. Closed → re-open rail;
                open·dock → 322px. Overlay is rendered at page root below. */}
            {askOut && <AskEdgeTab working={askWorking} onFocusOther={focusAsk} onBringBack={askBack} />}
            {!askOut && !showAskLilia && (
              <button
                type="button"
                data-testid="ask-lilia-open"
                onClick={() => setShowAskLilia(true)}
                title={i18n.t("editor:ui.askLilia")}
                aria-label={i18n.t("editor:ui.openAskLilia")}
                className="shrink-0 flex flex-col items-center justify-center gap-2 border-l border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-surface)] hover:bg-[var(--lilia-bg-muted,#f1f5f9)]"
                style={{ width: REOPEN_RAIL_W }}
              >
                <IconSparkles size={16} className="text-[var(--lilia-accent,#6366f1)]" />
                <span style={{ writingMode: "vertical-rl" }} className="text-[10.5px] font-semibold tracking-wide text-[var(--lilia-text-secondary)]">
                  {i18n.t("editor:ui.askLilia")}
                </span>
              </button>
            )}
            {!askOut && showAskLilia && askMode === "dock" && (
              <div
                data-testid="ask-lilia-dock"
                className="shrink-0 overflow-hidden border-l border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-surface,#fff)]"
                style={{ width: 322 }}
              >
                <AskLiliaPanel
                  documentId={id ?? undefined}
                  onApplyLatex={applyLatexFromChat}
                  onClose={() => setShowAskLilia(false)}
                  onMove={(how) => void moveAsk(how)}
                  arrangement="side"
                  onArrange={setArrangement}
                  mode="dock"
                  onToggleMode={toggleAskMode}
                  syncEnabled
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* ToolboxMount lives in the in-flow layout (ribbon mockup). Chip /
          seed / header / FAB still render as fixed-position overlays. */}
      <KeyboardShortcutsDialog />
      <FeedbackDialog open={showFeedbackDialog} onClose={() => setShowFeedbackDialog(false)} />
      <DocumentSettingsDialog
        open={showSettingsDialog}
        onClose={() => setShowSettingsDialog(false)}
        onSave={handleSettingsSave}
        settings={{
          columns: docColumns,
          columnGap: docColumnGap,
          columnSeparator: docColumnSeparator,
          paperSize: document.paperSize ?? "a4",
          fontFamily: document.fontFamily ?? "serif",
          fontSize: document.fontSize ?? 12,
          experimentalLatexEdit: document.experimentalLatexEdit ?? false,
          customPreamble: document.customPreamble ?? "",
          look: document.look ?? null,
          lookLocked: document.lookLocked ?? null,
          // The layout the document has, so the dialog starts from it: page numbering was always
          // shown as Arabic and saved back as such, over a document set to Roman or none.
          orientation: document.orientation,
          marginTop: document.marginTop ?? undefined,
          marginBottom: document.marginBottom ?? undefined,
          marginLeft: document.marginLeft ?? undefined,
          marginRight: document.marginRight ?? undefined,
          headerText: document.headerText ?? undefined,
          footerText: document.footerText ?? undefined,
          headerLeft: document.headerLeft,
          headerCenter: document.headerCenter,
          headerRight: document.headerRight,
          footerLeft: document.footerLeft,
          footerCenter: document.footerCenter,
          footerRight: document.footerRight,
          lineSpacing: document.lineSpacing ?? undefined,
          paragraphIndent: document.paragraphIndent ?? undefined,
          pageNumbering: document.pageNumbering ?? undefined,
          headingNumbering: document.headingNumbering ?? null,
        }}
        chapters={chapters}
      />

      {/* Bottom-right "View source" FAB removed (founder 2026-06-29: "only
          show the preview source on the top menu, not the floating bottom
          one"). The TopBar's View-source toggle (⌘\) is the single affordance;
          it falls back to mode split↔visual which is exactly what this did. */}

      {/* FloatingToolbar intentionally unmounted (2026-07-16): selection
          bubble / context-style menu removed from Flow. */}

      {/* The tour (5d, Olivia 14 Oct): Blocks, Ask Lilia, Validate, Export, once per account;
          Help → Take the tour starts it again. Replaces the first-run coach. */}
      <Tour mayEdit={mayEdit} role={(document as { role?: string | null }).role} ownerName={(document as { ownerName?: string | null }).ownerName} />

      {/* Ask Lilia — docked + closed-rail render IN-FLOW inside the body row
          above (so they sit below the chrome, CD §1). Only the floating
          OVERLAY card is rendered here at the page root. */}
      {/* Open · overlay → floating 346px card (Side layout only; Chat-primary
          already is the main surface). */}
      {!askOut && !isChatLayout && showAskLilia && askMode === "overlay" && (
        <>
          <div
            className="fixed inset-0 z-40 dark:bg-black/10"
            onClick={() => setShowAskLilia(false)}
            data-testid="ask-lilia-backdrop"
          />
          <div
            data-testid="ask-lilia-overlay"
            className="fixed z-50 overflow-hidden rounded-[14px] border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-surface,#fff)] shadow-2xl"
            style={{ top: 12, right: 12, bottom: 12, width: 346 }}
          >
            <AskLiliaPanel documentId={id ?? undefined} onClose={() => setShowAskLilia(false)} onMove={(how) => void moveAsk(how)} arrangement="side" onArrange={setArrangement} mode="overlay" onToggleMode={toggleAskMode} syncEnabled onApplyLatex={applyLatexFromChat} />
          </div>
        </>
      )}

      {/* StatusBar render moved 2026-05-09 (Pass 3 / Implementation
          guidance #6): the status whisper now sits below the document
          inside the scroll container, NOT as a chrome strip. The data
          is pushed into editorStore.statusMeta and BlockEditor mounts
          the <StatusBar /> at the bottom of its blockList. */}
    </div>
  );
}

export function EditorPage() {
  return (
    <EditorProvider>
      {/* StudioFormattingProvider was previously only mounted in
          StudioPage; FormatRailPanel and any future formatting
          surfaces in Flow mode need the same context to find the
          active TipTap editor. Without this wrapper, opening the
          Format rail tab (Mod+4) crashes with "useStudioFormatting
          must be used within StudioFormattingProvider". */}
      <StudioFormattingProvider>
        <EditorPageInner />
      </StudioFormattingProvider>
    </EditorProvider>
  );
}

export default EditorPage;

/**
 * Word counter for the status whisper.
 *
 * Walks every block's content tree and totals whitespace-delimited words from
 * any string field. Skips structural / non-text blocks (figure, table, code,
 * page-break, equation) so a code block with 80 lines doesn't drown out the
 * actual prose count. Inline formatting (bold/italic/code) lives inside
 * paragraph content as plain strings inside ProseMirror JSON, so a recursive
 * walk surfaces it without needing to know the schema.
 */
function countWordsInBlocks(blocks: { type: string; content: Record<string, unknown> }[]): number {
  const TEXT_TYPES = new Set([
    "paragraph", "heading", "list", "blockquote", "abstract", "theorem",
  ]);
  let total = 0;
  const walk = (node: unknown) => {
    if (node == null) return;
    if (typeof node === "string") {
      const words = node.trim().split(/\s+/).filter(Boolean);
      total += words.length;
      return;
    }
    if (Array.isArray(node)) {
      for (const child of node) walk(child);
      return;
    }
    if (typeof node === "object") {
      for (const value of Object.values(node as Record<string, unknown>)) {
        walk(value);
      }
    }
  };
  for (const block of blocks) {
    if (!TEXT_TYPES.has(block.type)) continue;
    walk(block.content);
  }
  return total;
}
